/**
 * Gift of Kaw — Lila designer-matching quiz frontend
 *
 * Separate from assets/quiz.js on purpose: Lila's flow has no product, no
 * fixed question set, and returns one to three DESIGNERS. assets/quiz.js
 * serves three live pages and is not worth generalising for this.
 *
 * Expected endpoints:
 *   GET  /api/lila-quiz?fingerprint=...
 *   POST /api/lila-quiz        { action: 'start'|'answer', message?, history, fingerprint }
 *   POST /api/lila-quiz-match  { history, fingerprint }
 *
 * Both POST endpoints may return JSON or SSE using `stage`, `result` and
 * `error` events. The mock path exists only so the frontend can be built and
 * reviewed before those endpoints are implemented.
 *
 * ── HER PLAY IS NOT HER SISTERS' ──────────────────────────────────────────
 * Poking works the way it does everywhere else. DRAGGING does not. Rosa can
 * be lifted; Azura is rooted and trembles against the grip; Akira glitches
 * out of it. Lila DECLINES — see fireDecline(). She does not move, she does
 * not strain, she does not repeat: she swaps to a refusal drawing, says one
 * line, and stops accepting input entirely until she is done. Then she goes
 * back to work. That is the whole difference between "she is resisting you"
 * and "she is a professional who said no".
 */
(function () {
  'use strict';

  /* ── Activation ────────────────────────────────────────────────────────
     Chrome refuses navigator.vibrate() until the document has been tapped,
     and for TOUCH that activation lands on pointerup/touchend — never on
     pointerdown. Sticky once true, so this only ever bites the first gesture
     after a load. */
  var _tapped = false;
  ['pointerup', 'touchend', 'click', 'keydown'].forEach(function (type) {
    document.addEventListener(type, function () { _tapped = true; }, true);
  });
  function pageActivated() {
    var ua = navigator.userActivation;
    if (ua && typeof ua.hasBeenActive === 'boolean') return ua.hasBeenActive;
    return _tapped;
  }

  /* A short buzz confirming a deliberate tap on one of the quiz's own controls.
     Mirrors tapHaptic() in assets/quiz.js so both quizzes feel the same under a
     thumb. Desktop is excluded by the pointer query rather than by width: a
     touchscreen laptop should buzz, a narrow desktop window should not. */
  var COARSE = !!(window.matchMedia && window.matchMedia('(any-pointer:coarse)').matches);

  /* The visitor's name (home «Carta de la editora», `gokExLibris`), sent as `visitorName` with
     the interview (never the match): the backend keeps it only if it is one word of letters,
     and she skips it if it is a joke (never a reason for [BAN]). */
  function getVisitorName() {
    try { return String(localStorage.getItem('gokExLibris') || '').slice(0, 64); } catch (e) { return ''; }
  }

  var fingerprintPromise = null;

  function getFingerprint() {
    if (fingerprintPromise) return fingerprintPromise;
    fingerprintPromise = new Promise(function (resolve) {
      try {
        var signals = [];
        try {
          var canvas = document.createElement('canvas');
          canvas.width = 200;
          canvas.height = 50;
          var ctx = canvas.getContext('2d');
          ctx.textBaseline = 'top';
          ctx.font = '14px Arial';
          ctx.fillStyle = '#a855f7';
          ctx.fillRect(0, 0, 100, 50);
          ctx.fillStyle = '#fff';
          ctx.fillText('GoK Lila quiz', 3, 16);
          signals.push(canvas.toDataURL());
        } catch (error) {
          signals.push('canvas-error');
        }
        signals.push((Intl.DateTimeFormat().resolvedOptions().timeZone) || 'tz');
        signals.push(navigator.language || 'lang');
        signals.push(screen.width + 'x' + screen.height + 'x' + screen.colorDepth);
        signals.push(navigator.platform || 'platform');

        if (!window.crypto || !crypto.subtle || typeof TextEncoder === 'undefined') {
          resolve(null);
          return;
        }
        crypto.subtle.digest('SHA-256', new TextEncoder().encode(signals.join('|')))
          .then(function (buffer) {
            resolve(Array.from(new Uint8Array(buffer)).map(function (byte) {
              return byte.toString(16).padStart(2, '0');
            }).join(''));
          })
          .catch(function () { resolve(null); });
      } catch (error) {
        resolve(null);
      }
    });
    return fingerprintPromise;
  }

  function streamSSE(url, body, onStage) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      var contentType = response.headers.get('content-type') || '';
      if (contentType.indexOf('text/event-stream') === -1 || !response.body) {
        return response.json().then(function (data) {
          data._httpStatus = response.status;
          return data;
        }).catch(function () {
          return { success: false, message: 'No pude leer la respuesta del servidor.', _httpStatus: response.status };
        });
      }

      var reader = response.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';
      var eventName = '';
      var finalResult = null;

      function parseLine(line) {
        var clean = line.trim();
        if (clean.indexOf('event:') === 0) {
          eventName = clean.slice(6).trim();
          return;
        }
        if (clean.indexOf('data:') !== 0) return;
        try {
          var data = JSON.parse(clean.slice(5).trim());
          if (eventName === 'stage' && data.stage) {
            if (onStage) onStage(data.stage);
          } else if (eventName === 'result' || eventName === 'error') {
            finalResult = data;
            finalResult._httpStatus = response.status;
          }
        } catch (error) {
          /* Incomplete SSE line; the next chunk may finish it. */
        }
      }

      function pump() {
        return reader.read().then(function (chunk) {
          if (chunk.done) {
            if (buffer.trim()) parseLine(buffer);
            return finalResult || { success: false, message: 'El servidor no devolvió un resultado.' };
          }
          buffer += decoder.decode(chunk.value, { stream: true });
          var lines = buffer.split('\n');
          buffer = lines.pop();
          lines.forEach(parseLine);
          return pump();
        });
      }

      return pump();
    });
  }

  /* ══ POSES ═══════════════════════════════════════════════════════════════
     Two sources of art, one controller.

       · SETTINGS art — one image_picker per state, rendered by the section as
         <img data-lq-sprite="pose">. A pose holding several of them (poke,
         decline) is a POOL and shows one at random.
       · GROUP art — "Pose — …" theme blocks, each holding "Fotograma" blocks.
         Liquid cannot read a theme block's children, so every frame publishes
         itself into [data-lq-posedata] and the flipbook is assembled here.

     🔑 A GROUP WINS ITS POSE. A pose with a frame group ignores its single
     image; a pose without one behaves exactly as it did before groups existed.
     That is what lets this ship one pose at a time instead of all at once.

     A pose with no art at all borrows another's (FALLBACK), so the page still
     works before any of the drawing is done. */
  function setupPoses(root, cfg, reduce) {
    var sp = (cfg && cfg.sprites) || {};
    /* 🚨 isNaN, not `||`. A blend of 0 is a REAL setting — it means "hard cut"
       — and `||` would quietly swap a deliberate zero for the default. This
       project has been bitten by falsy-zero twice; the section emits these
       raw (no Liquid `default:`) for the same reason. */
    var blendMs = parseInt(sp.blendMs, 10);   if (isNaN(blendMs)) blendMs = 90;
    var blendBlur = parseFloat(sp.blendBlur); if (isNaN(blendBlur)) blendBlur = 2;
    var defaultMs = parseInt(sp.frameMs, 10); if (isNaN(defaultMs) || defaultMs <= 0) defaultMs = 120;
    if (reduce) { blendMs = 0; blendBlur = 0; }

    var fig = root.querySelector('[data-lq-fig]');
    var pools = {};
    var every = [];                              /* every sprite <img>, ever */
    Array.prototype.forEach.call(root.querySelectorAll('[data-lq-sprite]'), function (img) {
      var pose = img.getAttribute('data-lq-sprite');
      (pools[pose] || (pools[pose] = [])).push(img);
      every.push(img);
    });

    /* ── What the pose groups published ──────────────────────────────────── */
    var anim = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-lq-poseset]'), function (set) {
      var pose = set.getAttribute('data-lq-poseset');
      if (!pose) return;
      var frames = [], holds = [], size = null;
      Array.prototype.forEach.call(set.querySelectorAll('[data-lq-frame]'), function (f) {
        var src = f.getAttribute('data-src');
        if (!src) return;                        /* a frame with no image yet */
        frames.push(src);
        holds.push(Math.max(1, parseInt(f.getAttribute('data-hold'), 10) || 1));
        if (!size) {
          var w = parseInt(f.getAttribute('data-w'), 10);
          var h = parseInt(f.getAttribute('data-h'), 10);
          if (w && h) size = { w: w, h: h };
        }
      });
      if (!frames.length) return;                /* empty group → ignore it */
      var a = anim[pose];
      /* Two groups of the same pose concatenate, in document order. */
      if (a) { a.frames = a.frames.concat(frames); a.holds = a.holds.concat(holds); a.seq = null; return; }
      anim[pose] = {
        frames: frames, holds: holds, size: size,
        ms: parseInt(set.getAttribute('data-ms'), 10) || 0,
        pingpong: set.getAttribute('data-pingpong') === 'true',
        random: set.getAttribute('data-random') === 'true',
        img: null, seq: null, last: null
      };
    });

    /* Each grouped pose needs exactly one <img> to play into. Reuse the one the
       section already rendered for that pose when there is one, so the group
       simply takes the same element over. */
    Object.keys(anim).forEach(function (pose) {
      var a = anim[pose];
      var img = pools[pose] && pools[pose][0];
      if (!img && fig) {
        img = document.createElement('img');
        img.setAttribute('data-lq-sprite', pose);
        img.setAttribute('alt', '');
        img.setAttribute('aria-hidden', 'true');
        img.setAttribute('decoding', 'async');
        /* 🚨 width/height from the ARTWORK, never a guess. Her drawings are not
           all the same aspect, so one hardcoded pair reserves the wrong box for
           most of them and shifts the layout as each one loads. */
        if (a.size) { img.setAttribute('width', a.size.w); img.setAttribute('height', a.size.h); }
        img.src = a.frames[0];
        fig.appendChild(img);
        every.push(img);
      }
      if (!img) return;
      a.img = img;
      pools[pose] = [img];                       /* the group replaces the single image */
    });

    /* If every drawing arrived through a group there is no sizer yet, and the
       "L" placeholder is still on screen. Promote the first image into flow and
       take the placeholder down — without a sizer .lq-fig has no height at all
       and she stays invisible even though her art loaded fine. */
    if (fig && !fig.querySelector('img.lq-sizer')) {
      var first = fig.querySelector('img[data-lq-sprite]');
      if (first) {
        first.classList.add('lq-sizer');
        first.removeAttribute('aria-hidden');
        first.setAttribute('alt', 'Lila');
        var ph = fig.querySelector('[data-lq-placeholder]');
        if (ph) ph.style.display = 'none';
      }
    }

    /* Preload every frame so a switch never shows an empty box. */
    Object.keys(anim).forEach(function (pose) {
      anim[pose].frames.forEach(function (u) { var pre = new Image(); pre.src = u; });
    });

    /* ── Frame blending ───────────────────────────────────────────────────
       Advancing a flipbook is `img.src = next`, which repaints instantly — the
       CSS opacity transition on these images only ever fires on a POSE switch,
       never between frames. Hand-drawn frames never register perfectly, so that
       hard cut reads as a jitter. One ghost layer holds the OUTGOING frame and
       dissolves over the incoming one, which is already at full opacity
       underneath, so the composite never dips and you never see the background
       through both. `blendMs: 0` restores the hard cut. */
    var SHADOW = 'drop-shadow(0 6px 18px rgba(0,0,0,.5))';   /* matches .lq-fig img */
    var ghost = null;
    if (fig && blendMs > 0) {
      ghost = document.createElement('img');
      ghost.className = 'lq-ghost';
      ghost.setAttribute('alt', '');
      ghost.setAttribute('aria-hidden', 'true');
      fig.appendChild(ghost);                    /* last child → paints on top */
    }
    function clearGhost() {
      if (!ghost) return;
      ghost.style.transition = 'none';
      ghost.style.opacity = '0';
    }
    function paint(img, url, dissolve) {
      if (!img || !url) return;
      var outgoing = img.getAttribute('src');
      img.src = url;
      if (!dissolve || !ghost || !outgoing || outgoing === url) return;
      ghost.src = outgoing;
      ghost.style.transition = 'none';
      ghost.style.opacity = '1';
      ghost.style.filter = blendBlur ? SHADOW + ' blur(' + blendBlur + 'px)' : SHADOW;
      /* Force the reset to land before the fade starts, or the browser folds
         both writes into one frame and nothing ever dissolves. */
      void ghost.offsetWidth;
      ghost.style.transition = 'opacity ' + dissolve + 'ms linear';
      ghost.style.opacity = '0';
    }

    /* Frames expanded by their per-frame hold, then optionally ping-ponged. */
    function sequence(a) {
      if (a.seq) return a.seq;
      var seq = [];
      a.frames.forEach(function (url, i) {
        var beats = a.holds[i] || 1;
        for (var b = 0; b < beats; b++) seq.push(url);
      });
      /* The first and last frames are the turn-around points; repeating them
         would make the animation hitch at each end. */
      if (a.pingpong && a.frames.length > 2) seq = seq.concat(seq.slice(1, -1).reverse());
      a.seq = seq;
      return seq;
    }

    /* 🚨 No `locked` entry, and there is no `locked` pose to fall back FROM:
       the cooldown screen wears the RESULT pose. See VIEW_POSE below. */
    var FALLBACK = {
      idle: 'intro',
      thinking: 'idle',
      result: 'idle',
      poked: 'idle',
      decline: 'poked'
    };

    var current = null;
    var shownEl = null;
    var timer = null;

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
      clearGhost();
    }

    function resolve(pose) {
      var seen = {};
      var name = pose;
      while (name && !seen[name]) {
        if (pools[name] && pools[name].length) return name;
        seen[name] = true;
        name = FALLBACK[name];
      }
      /* 🚨 LAST RESORT, and it is load-bearing. Every chain ends at `intro`,
         which has no FALLBACK entry because it IS the root — and now that the
         flat sprite_* pickers are gone, nothing guarantees an intro group
         exists. Ship only "Pose — Escuchando" and every screen would resolve to
         null: nothing gets `.is-on`, every sprite stays at opacity:0, and the
         "L" placeholder has ALREADY been hidden by the promotion above — an
         empty, correctly-sized stage. Borrowing any pose that does have art is
         always better than showing her as a blank box. */
      for (var any in pools) {
        if (pools[any] && pools[any].length) return any;
      }
      return null;
    }

    function set(pose, force) {
      if (!force && pose === current) return;
      current = pose;
      var resolved = resolve(pose);
      if (!resolved) return;
      stop();

      var a = anim[resolved];
      var pick;
      if (a) {
        pick = a.img;
      } else {
        var pool = pools[resolved];
        pick = pool[0];
        if (pool.length > 1) {
          /* Never the same drawing twice running — with four refusals the point
             is that you get a different one. */
          var choices = pool.filter(function (img) { return img !== shownEl; });
          pick = choices[Math.floor(Math.random() * choices.length)] || pool[0];
        }
      }
      /* 🚨 A CLASS, not the `hidden` attribute. Exactly one <img> carries
         `.lq-sizer` and stays in flow to give `.lq-fig` its height; hiding it
         would collapse the stage, and putting a different aspect in flow per
         pose would make her change size every time she reacted.
         Toggled over EVERY sprite, not over `pools` — a group takes its pose's
         pool down to one element, and the images it displaced would otherwise
         keep whatever class they were last left with. */
      every.forEach(function (img) { img.classList.toggle('is-on', img === pick); });
      shownEl = pick;

      if (!a) return;

      /* A RANDOM group is a reaction, not a loop: one drawing per firing, and
         never the same one twice running. */
      if (a.random) {
        var next = a.frames[0];
        if (a.frames.length > 1) {
          var others = a.frames.filter(function (u) { return u !== a.last; });
          next = others[Math.floor(Math.random() * others.length)] || a.frames[0];
        }
        a.last = next;
        paint(a.img, next, 0);
        return;
      }

      var seq = sequence(a);
      paint(a.img, seq[0], 0);
      /* Reduced motion holds frame 1 rather than flipping — the same line
         assets/quiz.js keeps for her sisters. Zeroing the blend alone was not
         enough: the flipbook itself is the motion. */
      if (reduce || seq.length < 2) return;
      var ms = Math.max(40, a.ms || defaultMs);
      /* A dissolve has to finish before the next frame lands, or the ghost never
         reaches zero and she just looks permanently smeared. */
      var dissolve = Math.min(blendMs, Math.round(ms * 0.85));
      var i = 0;
      timer = setInterval(function () {
        i = (i + 1) % seq.length;
        paint(a.img, seq[i], dissolve);
      }, ms);
    }

    return {
      set: set,
      current: function () { return current; },
      has: function (pose) { return !!resolve(pose); },
      /* Off-screen, nothing infinite may keep running. The CSS animations are
         handled by `.is-lq-still`, but a setInterval is invisible to CSS and
         would otherwise tick for the life of the page. */
      suspend: stop,
      resume: function () { if (current) set(current, true); }
    };
  }

  /* ══ PLAY: poke, and the refusal ════════════════════════════════════════ */
  function setupPlay(opts) {
    var fig = opts.fig, arena = opts.arena, poses = opts.poses, play = opts.play || {};
    /* Her whole zone. The bubble is positioned inside the arena but may rise
       above it, and this is how far — see placeBubble. Falls back to the arena
       if the markup ever changes, which just restores the old behaviour. */
    var stage = opts.stage || (arena && arena.closest ? arena.closest('.lq-stage') : null);
    var bubble = opts.bubble, bubbleText = opts.bubbleText;
    var restorePose = opts.restorePose, reduce = opts.reduce, haptics = opts.haptics;
    if (!fig || !arena || !play.enabled) return null;

    var POKE_SLOP = 10;        /* mouse: travel under this is a tap */
    var TOUCH_SLOP = 24;       /* finger: a tap drifts more than a cursor does */
    var DECLINE_MIN = 14;      /* travel that turns a press into an attempted drag */
    var ARM_X = 10;            /* sideways travel that commits a FINGER to a drag */
    var TAP_MS = 400;

    var REACTION_MS = Math.max(400, parseInt(play.reactionMs, 10) || 2600);
    var DECLINE_MS = Math.max(200, parseInt(play.declineMs, 10) || 760);
    var SQUASH_MS = 620;
    var SWAP_MS = 150;

    var pressing = false, pointerId = null, armed = false, touchPointer = false;
    var pressX = 0, pressY = 0, pressAt = 0;
    var touchFar = 0;
    var declined = false;      /* this gesture has already been answered */
    var blockedUntil = 0;      /* she is mid-refusal: no drags, no pokes */
    var swallowClick = false;
    var pokeTimer = null, declineTimer = null, bubbleTimer = null, swapTimer = null;
    var lastLine = null;

    fig.style.setProperty('--lq-decline-ms', DECLINE_MS + 'ms');

    function isBlocked() { return Date.now() < blockedUntil; }

    /* Returns false when the buzz did NOT happen, so a first-gesture refusal
       can be re-fired from pointerup where the activation has just landed.
       vibrate()'s own return value cannot tell us that — per spec it answers
       true while the browser is still suppressing for want of a tap. */
    function haptic(pattern) {
      if (!haptics || !touchPointer || typeof navigator.vibrate !== 'function') return null;
      if (!pageActivated()) return false;
      try { return navigator.vibrate(pattern) !== false; } catch (error) { return false; }
    }

    /* ── Her bubble ─────────────────────────────────────────────────────── */
    var BUBBLE_GAP = parseFloat(play.bubbleGap);
    if (!isFinite(BUBBLE_GAP)) BUBBLE_GAP = 6;
    var BUBBLE_PAD = 6;
    var bubbleRaf = null;

    /* WHERE SHE ACTUALLY IS — the painted art, not the box that holds it.
       🚨 `.lq-fig`'s box is NOT her drawing. The images are `height:100%;
       width:auto; max-width:100%; object-fit:contain`, so whenever the box is
       taller than her aspect allows, `max-width` clamps the width and `contain`
       scales the art down inside a full-height box, parked at the bottom by
       `object-position`. Measured on desktop: a 456.5px box holding 267.8px of
       Lila — **188.7px of empty letterbox above her head**, which is exactly
       how far the bubble was floating away from her.

       On a phone the box and the art coincide (aspect measured identical), so
       this returns the same rect there and changes nothing.

       Every one of her sprites is 620x693, so the sizer's aspect stands in for
       whichever pose is showing; it is also the image that defines the layout. */
    function artBox() {
      var img = fig.querySelector('img.lq-sizer') || fig.querySelector('img');
      if (!img || !img.naturalWidth || !img.naturalHeight) return fig.getBoundingClientRect();
      var r = img.getBoundingClientRect();
      if (!r.width || !r.height) return fig.getBoundingClientRect();
      var s = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
      var w = img.naturalWidth * s, h = img.naturalHeight * s;
      return {
        left: r.left + (r.width - w) / 2,       /* object-position: 50% 100% */
        right: r.left + (r.width + w) / 2,
        top: r.bottom - h,
        bottom: r.bottom,
        width: w,
        height: h
      };
    }

    function placeBubble() {
      if (!bubble || bubble.hidden) return;
      var a = arena.getBoundingClientRect();
      /* 🔑 THE CEILING IS THE STAGE, NOT THE ARENA.
         Her sisters' arena is ~26% taller than the drawing inside it (measured:
         Azura's is 352.8px around a 262.1px figure), so their bubble has 65px
         of room above her head WITHIN the arena and the clamp below never bites.
         Lila's `.lq-fig` is `height:100%` — it fills her arena exactly, so
         `f.top === a.top`, there is never any room, and the bubble was pinned to
         the arena's top edge ON her face: measured 67.6px of overlap.

         Her drawing is sized the way the user approved, so instead of shrinking
         her the bubble is allowed to use the empty stage ABOVE the arena — 53px
         in intro, 30px in chat, which was already there and simply unreachable.
         Safe because `.lq-stage`, `.lq-portrait` and `.lq-inner` are all
         `overflow:visible`; only `.lq-quiz` clips, and the stage is inside it. */
      var ceil = stage ? stage.getBoundingClientRect() : a;
      /* A smaller pad against the CEILING than against the arena's own edges:
         the stage's top is not a visual boundary — the frame's 14px inset sits
         above it — so coming within 2px of it clips nothing, and those 4px are
         the difference between clearing her hair and resting on it. */
      var minTop = (ceil.top - a.top) + (stage ? 2 : BUBBLE_PAD);   /* arena-local; usually negative */
      /* 🚨 Bound the TEXT, never `.lq-say` itself. The bubble owns its tail as an
         absolutely-positioned `::after` hanging 6px outside its padding box, and
         `.lq-say` is `position:absolute`, so ANY overflow clipping there is the
         containing block clipping its own tail away — which shipped once and
         turned every bubble into a plain rectangle pointing at nothing.
         Capping the span bounds the box just the same, because the bubble is
         only ever as tall as its text plus padding.
         18 = the 9px top + 9px bottom padding declared on `.lq-say`; the 12
         keeps 6px of slack at each end of the arena. Written only when it
         actually changes — this runs every animation frame while she talks. */
      if (bubbleText) {
        /* Measured from the CEILING to the arena's floor, which is the space
           the bubble may actually occupy now that it can rise past the arena. */
        var cap = Math.max(0, Math.round((a.bottom - ceil.top) - 12 - 18));
        if (bubbleText.__lqCap !== cap) {
          bubbleText.style.maxHeight = cap + 'px';
          bubbleText.__lqCap = cap;
        }
      }
      var f = artBox();
      var w = bubble.offsetWidth, h = bubble.offsetHeight;
      if (!w || !h) return;
      var cx = f.left + f.width / 2 - a.left;
      var left = Math.max(BUBBLE_PAD, Math.min(cx - w / 2, a.width - w - BUBBLE_PAD));
      var top = f.top - a.top - h - BUBBLE_GAP;
      var below = f.bottom - a.top + BUBBLE_GAP;
      /* Above her by default; under her only when there is genuinely no room
         up there AND there is room below, rather than clamping over her face. */
      var flip = top < minTop && below + h <= a.height - BUBBLE_PAD;
      if (flip) top = below;
      bubble.classList.toggle('is-below', flip);
      top = Math.max(minTop, Math.min(top, a.height - h - BUBBLE_PAD));
      bubble.style.left = Math.round(left) + 'px';
      bubble.style.top = Math.round(top) + 'px';
      bubble.style.setProperty('--lq-tail-x', Math.round(Math.max(14, Math.min(cx - left, w - 14))) + 'px');
    }
    function trackBubble() { placeBubble(); bubbleRaf = requestAnimationFrame(trackBubble); }
    function startTracking() { if (bubbleRaf === null) trackBubble(); }
    function stopTracking() {
      if (bubbleRaf !== null) { cancelAnimationFrame(bubbleRaf); bubbleRaf = null; }
    }

    function linesOf(lines) {
      return (Array.isArray(lines) ? lines : []).map(function (line) {
        return String(line).trim();
      }).filter(Boolean);
    }

    function say(lines) {
      if (!play.bubble || !bubble || !bubbleText) return;
      var pool = linesOf(lines);
      if (!pool.length) return;
      var line = pool[Math.floor(Math.random() * pool.length)];
      if (pool.length > 1 && line === lastLine) {
        line = pool[(pool.indexOf(line) + 1) % pool.length];
      }
      lastLine = line;
      if (!bubble.hidden && bubble.classList.contains('is-on')) {
        bubble.classList.add('is-swapping');
        if (swapTimer) clearTimeout(swapTimer);
        swapTimer = setTimeout(function () {
          swapTimer = null;
          bubbleText.textContent = line;
          bubble.classList.remove('is-swapping');
        }, reduce ? 0 : SWAP_MS);
      } else {
        bubbleText.textContent = line;
      }
      bubble.hidden = false;
      /* Placed BEFORE it becomes visible, or it fades in wherever the last
         reaction left it and slides across to her. */
      placeBubble();
      startTracking();
      requestAnimationFrame(function () { bubble.classList.add('is-on'); });
      fig.classList.add('is-talking');
      if (bubbleTimer) clearTimeout(bubbleTimer);
      bubbleTimer = setTimeout(hush, REACTION_MS);
    }

    function hush() {
      if (!bubble) return;
      fig.classList.remove('is-talking');
      if (swapTimer) { clearTimeout(swapTimer); swapTimer = null; }
      bubble.classList.remove('is-on', 'is-swapping');
      if (bubbleTimer) clearTimeout(bubbleTimer);
      bubbleTimer = setTimeout(function () {
        bubble.hidden = true;
        stopTracking();
      }, 260);
    }

    /* ── THE POKE ───────────────────────────────────────────────────────── */
    function poke() {
      haptic(10);
      poses.set('poked', true);
      /* Re-adding a class whose animation is still running is a no-op, so a
         quick second poke would land silently without this reflow. */
      fig.classList.remove('is-poked');
      void fig.offsetWidth;
      fig.classList.add('is-poked');
      setTimeout(function () { fig.classList.remove('is-poked'); }, SQUASH_MS);
      say(play.pokeLines);
      if (pokeTimer) clearTimeout(pokeTimer);
      pokeTimer = setTimeout(function () {
        pokeTimer = null;
        if (restorePose) restorePose();
      }, reduce ? 0 : REACTION_MS);
    }

    /* ── THE REFUSAL ─────────────────────────────────────────────────────
       She does not travel a single pixel, and nothing here repeats while you
       keep pulling: one drawing, one line, one gesture, and no input accepted
       until it finishes. Deliberately NOT Azura's tremble — a tremble reads
       as someone straining to hold their ground, and Lila is not straining.
       She is declining. */
    function fireDecline() {
      declined = true;
      blockedUntil = Date.now() + DECLINE_MS;
      /* End the gesture here. Anything else — more travel, the eventual
         pointerup — must not reach the poke path and answer her twice. */
      releaseGesture();
      swallowClick = true;

      var buzzed = haptic([14, 30, 14]);
      if (buzzed === false) {
        /* First gesture of a fresh page: the activation lands on the pointerup
           that has not happened yet. Try again on the way out. */
        pendingDeclineHaptic = true;
      }

      poses.set('decline', true);
      fig.classList.remove('is-declining', 'is-poked');
      arena.classList.remove('is-declining');
      void fig.offsetWidth;
      fig.classList.add('is-declining');
      arena.classList.add('is-declining');
      if (declineTimer) clearTimeout(declineTimer);
      declineTimer = setTimeout(function () {
        declineTimer = null;
        fig.classList.remove('is-declining');
        arena.classList.remove('is-declining');
      }, DECLINE_MS);

      say(play.declineLines && play.declineLines.length ? play.declineLines : play.pokeLines);

      /* The drawing outlives the gesture so the refusal can be read, but she
         is answering pokes again as soon as the gesture is over. */
      if (pokeTimer) clearTimeout(pokeTimer);
      pokeTimer = setTimeout(function () {
        pokeTimer = null;
        if (restorePose) restorePose();
      }, reduce ? 0 : REACTION_MS);
    }

    var pendingDeclineHaptic = false;

    function releaseGesture() {
      pressing = false;
      armed = false;
      if (fig.releasePointerCapture && pointerId != null) {
        try { fig.releasePointerCapture(pointerId); } catch (error) {}
      }
      pointerId = null;
      fig.classList.remove('is-grabbed');
    }

    function down(event) {
      if (isBlocked()) return;                       /* mid-refusal: she ignores you */
      if (event.button !== undefined && event.button !== 0) return;
      if (opts.canPlay && !opts.canPlay()) return;

      /* 🚨 A NEW PRESS SUPERSEDES A REACTION THAT IS STILL COUNTING DOWN — but
         `pokeTimer` is the ONLY thing that ever calls restorePose(), so
         clearing it on its own ORPHANS the pose. Hold your finger down past
         TAP_MS and up() correctly decides it was not a tap, so poke() never
         runs, so nothing reschedules the restore: she stays on the reaction
         drawing for the life of the page. Reproduced: poke, then press and
         hold — `Lila_poke_sprite_3.png` still showing 2s after release.

         Her sisters cancel and restore in the same breath (quiz.js:540-542),
         which is why this never happens to them.

         🔑 Restoring only when a reaction was actually pending, rather than on
         every press as theirs does: `restorePose()` passes `force:true`, and a
         forced set() calls stop() and rebuilds the flipbook from frame 0 — so
         an unconditional call here would restart her idle animation on every
         single touch. Theirs is a plain set() and does not have that cost. */
      if (pokeTimer) {
        clearTimeout(pokeTimer);
        pokeTimer = null;
        if (restorePose) restorePose();
      }
      pressing = true;
      declined = false;
      touchPointer = event.pointerType === 'touch' || event.pointerType === 'pen';
      pointerId = event.pointerId;
      pressX = event.clientX;
      pressY = event.clientY;
      pressAt = Date.now();
      touchFar = 0;
      /* Only a finger has to earn the drag. A mouse arms at once: there is no
         scroll competing for the gesture, and desktop must still be able to
         try to drag her straight up. */
      armed = !touchPointer;
      fig.classList.add('is-grabbed');
      /* MOUSE ONLY. 🚨 A touch pointer already holds implicit capture from
         pointerdown; asking for it again RE-TARGETS it and fires
         lostpointercapture, which this file reads as "the gesture is over". */
      if (armed && fig.setPointerCapture && pointerId != null) {
        try { fig.setPointerCapture(pointerId); } catch (error) {}
      }
    }

    function move(event) {
      if (!pressing || declined) return;
      var px = event.clientX - pressX;
      var py = pressY - event.clientY;
      if (!armed) {
        /* Sideways, and MORE sideways than up or down. On a diagonal the
           browser takes the gesture for a scroll, so arming there would hand
           her a refusal for a page scroll the visitor never aimed at her. */
        if (Math.abs(px) < ARM_X || Math.abs(px) <= Math.abs(py)) return;
        armed = true;
      }
      if (Math.hypot(px, py) > DECLINE_MIN) fireDecline();
    }

    function up(event) {
      if (declined) {
        /* Already answered on the way in. The buzz may have been refused for
           want of an activation — pointerup has just granted it. */
        if (pendingDeclineHaptic) { haptic([14, 30, 14]); pendingDeclineHaptic = false; }
        declined = false;
        touchPointer = false;
        return;
      }
      if (!pressing) return;
      var wasTouch = touchPointer;
      /* 🚨 On touch the POINTER stream stops at a pointercancel, so the last
         pointer position cannot tell a tap from a scroll. touchmove keeps
         firing throughout, so `touchFar` is the authority there — and BOTH
         the distance and the 400ms window have to hold, or a slow drag that
         happens to end near where it started reads as a poke. */
      var moved = wasTouch
        ? Math.max(touchFar, event ? Math.hypot(pressX - event.clientX, pressY - event.clientY) : 0)
        : (event ? Math.hypot(pressX - event.clientX, pressY - event.clientY) : Infinity);
      var slop = wasTouch ? TOUCH_SLOP : POKE_SLOP;
      var quick = Date.now() - pressAt < TAP_MS;
      releaseGesture();
      touchPointer = wasTouch;                       /* haptic() reads this */
      if (moved < slop && quick) poke();
      touchPointer = false;
    }

    /* A state change can land mid-gesture. Put her down rather than let the
       press survive into a screen that is no longer hers. Deliberately not
       up(): that would read a barely-moved grab as a poke. */
    function cancel() {
      if (!pressing) { hush(); return; }
      declined = false;
      releaseGesture();
      touchPointer = false;
      hush();
    }

    fig.addEventListener('pointerdown', down);
    fig.addEventListener('pointermove', move);
    fig.addEventListener('pointerup', up);
    fig.addEventListener('pointercancel', up);
    fig.addEventListener('lostpointercapture', function () { if (pressing) up(null); });
    /* touchFar is the only honest record of how far a finger travelled once
       the browser has taken the gesture for a scroll. Passive: we never
       preventDefault here — pan-y is doing that job in CSS. */
    fig.addEventListener('touchstart', function () { touchFar = 0; }, { passive: true });
    fig.addEventListener('touchmove', function (event) {
      var t = event.touches && event.touches[0];
      if (t) touchFar = Math.max(touchFar, Math.hypot(t.clientX - pressX, t.clientY - pressY));
    }, { passive: true });
    /* A refused drag must not also fire the click it ends in. Armed only by
       fireDecline, so an ordinary mouse click on the page still works. */
    fig.addEventListener('click', function (event) {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    fig.addEventListener('dragstart', function (event) { event.preventDefault(); });

    var wasTracking = false;
    return {
      say: say,
      hush: hush,
      cancel: cancel,
      suspend: function () { wasTracking = bubbleRaf !== null; stopTracking(); },
      resume: function () { if (wasTracking) startTracking(); wasTracking = false; }
    };
  }

  /* ══ THE LETTER'S MOTION — how one screen becomes the next (2026-10-02) ══
     User: "add some transition animations between the states … when you start
     the quiz, moving through sections … the other girls had something, some
     smooth element changing, but Lila doesn't have that." Her sisters fade
     every face in (`cq-fade`, character-quiz.liquid:683) and glide the chibi
     between sizes (`.cq-fig{transition:.6s}`); Lila's screens were bare
     `hidden` toggles, so every step was a hard cut.

     Hers move like PAPER, the vocabulary of the envelope she lives in:
       · a screen on the sheet LIFTS AWAY and the next one is WRITTEN IN, line
         by line — label, title, text, then its objects;
       · the sheet SLIDES BACK INTO THE ENVELOPE when she takes the whole pane
         to write, and is DRAWN OUT again when the pane is the sheet's;
       · questions TURN like pages, in the direction you travel;
       · stamps and postmarks are STAMPED (a press that lands); her sent
         letters are DEALT onto the sheet;
       · Lila never jumps: when she is on screen before AND after a change she
         GLIDES there (FLIP on her arena) — the smooth change her sisters'
         chibi has. When the figure changes identity (Lila ⇄ a designer), the
         one leaves and the other rises in her light.

     🔑 TWO PHASES, LOGIC FIRST. setView() still updates `state.view` and
        everything the logic reads at once; only the DOM switch waits for the
        outgoing half (≤280ms). The one caller that needed the new screen
        immediately — focusing the composer — rides setView's `after`.
     🔑 ONLY transform AND opacity move, through the INDIVIDUAL `translate` /
        `scale` / `rotate` properties, so a CSS `transform` animation (her
        breathing) keeps running underneath and a stamp keeps its CSS tilt.
        The sheet is grained, blended paper: animating its SIZE re-rasterises
        it every frame on a phone (the FAQ letter's lesson). Layout changes
        land in one step and FLIP makes them read as continuous.
     🔑 IMPLICIT KEYFRAMES — an entrance gives only its `offset:0` frame and an
        exit only its `offset:1` frame, so the other end is whatever the CSS
        says: the ring's .55 on the result, the designer's `translate:-50% 0`
        centring, a disabled button's .42. Nothing here restates a state.
     🔑 OFF when it must not move or cannot be seen: reduced motion, before
        the visitor's first touch of the quiz (a screen restored on load is
        not a transition), quiz scrolled away, tab hidden. Then it is the old
        cut, synchronously.
     🔑 setTimeout, never `animation.finished`, drives the switch: a hidden tab
        or pane does not advance the animation timeline, and the screen must
        change regardless. Every new change FLUSHES the one in flight first
        (its switch lands, its animations end), so quick taps can never leave
        a half-written screen. */
  function setupMotion(root, reduce, isQuiet, onSettle) {
    var supported = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
    var armed = false;
    var job = null;
    var OUT = 'cubic-bezier(.45,0,.9,.55)';
    var IN = 'cubic-bezier(.2,.8,.25,1)';
    var GLIDE = 'cubic-bezier(.32,.86,.3,1)';
    var STAMP = 'cubic-bezier(.3,.75,.35,1)';
    var DESK = window.matchMedia ? window.matchMedia('(min-width:901px)') : null;
    var PANEL = { intro: 1, sheet: 1, chat: 1, locked: 1, error: 1 };

    var $ = function (sel) { return root.querySelector(sel); };
    var E = {
      panel: $('.lq-panel'),
      stage: $('.lq-stage'),
      idrow: $('.lq-idrow'),
      role: $('[data-lq-stage-role]'),
      portrait: $('[data-lq-arena]'),
      lila: $('[data-lq-fig]'),
      dz: $('[data-lq-designer-fig]'),
      halo: $('.lq-halo'),
      ring: $('.lq-ring'),
      work: $('[data-lq-work]'),
      showHead: $('[data-lq-show-head]'),
      show: $('[data-lq-show]'),
      stepLabel: $('[data-lq-steplabel]')
    };

    function arm() { armed = true; }
    root.addEventListener('pointerdown', arm, true);
    root.addEventListener('keydown', arm, true);

    function live() {
      return supported && !reduce && armed && document.visibilityState !== 'hidden' && !(isQuiet && isQuiet());
    }
    /* On screen = rendered, not merely un-hidden: a view inside a
       `display:none` panel has no boxes, and neither does Lila on the result. */
    function shown(el) { return !!el && !el.hidden && el.getClientRects().length > 0; }
    function rect(el) { return el.getBoundingClientRect(); }
    function kids(el) { return el ? Array.prototype.slice.call(el.children) : []; }
    function stop(a) { if (a) { try { a.cancel(); } catch (error) {} } }
    function px(n) { return n.toFixed(1) + 'px'; }

    function exitAnim(el, frame, ms) {
      if (!el) return null;
      frame.offset = 1;
      return el.animate([frame], { duration: ms, easing: OUT, fill: 'forwards' });
    }
    function enterAnim(el, frame, ms, delay, easing) {
      if (!el) return null;
      frame.offset = 0;
      return el.animate([frame], { duration: ms, delay: delay || 0, easing: easing || IN, fill: 'backwards' });
    }
    function stampAnim(el, delay) {
      return el.animate([
        { opacity: 0, scale: '1.18', offset: 0 },
        { opacity: 1, scale: '.97', offset: .62 },
        { opacity: 1, scale: '1', offset: 1 }
      ], { duration: 380, delay: delay, easing: STAMP, fill: 'backwards' });
    }
    /* Translate-only FLIP: the box lands at its new place in one layout and is
       drawn from the old one. `axis` 'y' ignores a sideways shift — the name
       plate is centred, so its x moves whenever the role text changes length,
       and sliding it sideways for that would read as a wobble. */
    function glide(el, from, to, ms, axis) {
      var dx = axis === 'y' ? 0 : from.left - to.left;
      var dy = from.top - to.top;
      if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return null;
      return el.animate([{ translate: px(dx) + ' ' + px(dy), offset: 0 }], { duration: ms, easing: GLIDE });
    }

    /* Who is on screen in a state — the CSS's own rules, read as a table so
       the outgoing half can start before the switch. Checked against the real
       DOM after the switch, so a wrong guess only skips an entrance. */
    function cast(view) {
      var dz = view === 'results' && root.classList.contains('has-dzfig');
      return {
        panel: !!PANEL[view],
        idrow: !!PANEL[view],
        lila: !dz,
        dz: dz,
        work: view === 'sealing',
        show: view === 'results',
        paper: view === 'results' && !!(DESK && DESK.matches)
      };
    }

    /* The lines of a screen, in reading order, and how each one arrives. */
    function lines(view) {
      var list = [];
      kids(view).forEach(function (c) {
        if (!shown(c)) return;
        if (c.matches('.lq-ritual, .lq-countdown')) kids(c).forEach(function (k) { list.push([k, 'stamp']); });
        else if (c.matches('.lq-sent')) kids(c).forEach(function (k) { list.push([k, 'deal']); });
        else if (c.matches('.lq-q')) {
          kids(c).forEach(function (k) {
            if (!shown(k)) return;
            if (k.matches('.lq-opts')) kids(k).forEach(function (o) { list.push([o, 'opt']); });
            else list.push([k, 'line']);
          });
        }
        /* The transcript writes itself in, one `lq-in` per message. */
        else if (!c.matches('.lq-messages, .lq-typing')) list.push([c, 'line']);
      });
      return list;
    }

    function write(list, delay, dir) {
      var anims = [];
      var t = delay;
      var end = 0;
      list.forEach(function (pair) {
        var el = pair[0];
        var kind = pair[1];
        var a;
        var ms;
        if (kind === 'stamp') { ms = 360; a = stampAnim(el, t); t += 70; }
        else if (kind === 'deal') { ms = 440; a = enterAnim(el, { opacity: 0, translate: '26px 4px', rotate: '3deg' }, ms, t); t += 80; }
        else if (kind === 'opt') { ms = 320; a = enterAnim(el, { opacity: 0, translate: dir ? px(20 * dir) + ' 0px' : '0px 10px' }, ms, t); t += 30; }
        else { ms = 420; a = enterAnim(el, { opacity: 0, translate: dir ? px(20 * dir) + ' 0px' : '0px 14px' }, ms, t); t += 45; }
        anims.push(a);
        end = Math.max(end, t + ms);
      });
      return { anims: anims, end: end };
    }

    /* 🚨 A LINE IN FLIGHT IS OVERFLOW. Transforms count toward a scroll
       container's scrollable area, so a line rising 14px from below (or an
       option sliding in from the side) can push a sheet that exactly fits past
       its box, and a scrollbar flashes for half a second — her sisters' quiz
       did exactly that on desktop (user, 2026-10-02: "I can see a scroll
       inside the text container when the animation goes on"). While a screen
       writes in, its view — when its content already FITS — is set to
       `overflow:hidden` (nothing to scroll there anyway) and given back when
       the motion settles. A view that genuinely scrolls (the phone sheet)
       keeps its scrollbar: hiding it would reflow the text sideways. */
    var hushed = [];
    function hush(el) {
      if (!el || hushed.some(function (h) { return h[0] === el; })) return;
      var cs = getComputedStyle(el);
      if (!/(auto|scroll)/.test(cs.overflowX + ' ' + cs.overflowY)) return;
      if (el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1) return;
      hushed.push([el, el.style.overflow]);
      el.style.overflow = 'hidden';
    }
    function unhush() {
      hushed.forEach(function (h) { h[0].style.overflow = h[1]; });
      hushed = [];
    }

    function flush() {
      var j = job;
      if (!j) return;
      job = null;
      if (j.timer) window.clearTimeout(j.timer);
      j.timer = 0;
      if (!j.applied) { j.applied = true; j.apply(); }
      j.anims.forEach(stop);
      j.anims = [];
      j.inert.forEach(function (el) { el.inert = false; });
      clearPaper();
      unhush();
      root.classList.remove('lq-mo-paper-out');
      if (onSettle) onSettle();
    }

    function clearPaper() {
      root.classList.remove('lq-mo-paper-in', 'lq-mo-paper-morph');
      ['--lq-mo-x', '--lq-mo-y', '--lq-mo-sx', '--lq-mo-sy'].forEach(function (name) { root.style.removeProperty(name); });
    }

    function begin(kind, apply, anims, inert, wait, land) {
      var j = { kind: kind, apply: apply, applied: false, anims: anims, inert: inert, timer: 0 };
      job = j;
      j.timer = window.setTimeout(function () {
        j.timer = 0;
        if (job !== j) return;
        land(j);
      }, wait);
      return j;
    }

    /* The switch has landed and its entrances are running: the job stays "in
       flight" until they end, so a flush can finish them on demand.
       `onSettle` re-measures whatever reads the screen's scroll extent — a line
       still rising 14px below its place counts as overflow while it moves, so
       a "there is more" cue sampled at the switch can be a false one. */
    function settle(j, ms) {
      j.timer = window.setTimeout(function () {
        if (job !== j) return;
        job = null;
        clearPaper();
        unhush();
        if (onSettle) onSettle();
      }, ms + 40);
    }

    function swapIn(j, apply) {
      j.applied = true;
      apply();
      j.anims.forEach(stop);
      j.anims = [];
      j.inert.forEach(function (el) { el.inert = false; });
      j.inert = [];
    }

    /* Where the result's letter column is painted. It is the stage's own
       ::before (a grid item spanning column 1), which has no box of its own
       to measure — so it is derived from the stage grid it lives in. */
    function paperRect() {
      var r = rect(E.stage);
      var cs = getComputedStyle(E.stage);
      var pt = parseFloat(cs.paddingTop) || 0;
      var pb = parseFloat(cs.paddingBottom) || 0;
      return {
        left: r.left + (parseFloat(cs.paddingLeft) || 0),
        top: r.top + pt,
        width: parseFloat(String(cs.gridTemplateColumns).split(' ')[0]) || r.width,
        height: r.height - pt - pb
      };
    }

    /* ── One screen to another ─────────────────────────────────────────── */
    function change(from, to, apply) {
      flush();
      if (from === to || !live()) { apply(); return; }

      var before = {
        panel: shown(E.panel), idrow: shown(E.idrow), lila: shown(E.lila), dz: shown(E.dz),
        work: shown(E.work), show: shown(E.show),
        paper: root.getAttribute('data-lq-state') === 'results' && !!(DESK && DESK.matches)
      };
      var after = cast(to);
      /* 🔑 ONE SHEET, TWO PLACES (desktop). The result's letter column and the
         sheet are the same paper, so between them it MORPHS — the sheet
         becomes the letter beside the designer, the letter becomes the sheet
         again for the pause — instead of one fading out over an empty
         envelope and the other fading in. */
      var morphToPaper = before.panel && after.paper;
      var morphToPanel = before.paper && after.panel;
      var fromView = root.querySelector('[data-lq-view="' + from + '"]');
      var anims = [];
      var inert = [];
      var wait = 0;
      function exit(el, frame, ms) {
        var a = exitAnim(el, frame, ms);
        if (a) { anims.push(a); wait = Math.max(wait, ms); }
      }
      function freeze(el) { if (el && !el.inert) { el.inert = true; inert.push(el); } }

      if (before.panel && !after.panel && !morphToPaper) {
        /* Back into the envelope: down, a touch smaller, gone. */
        exit(E.panel, { opacity: 0, translate: '0px 22px', scale: '.97' }, 240);
        freeze(E.panel);
      } else if (before.panel && shown(fromView)) {
        /* The written page lifts away (the sheet itself stays). */
        exit(fromView, { opacity: 0, translate: '0px -10px' }, 150);
        freeze(fromView);
      }
      if (before.idrow && !after.idrow) exit(E.idrow, { opacity: 0 }, 180);
      var swap = (before.lila && !after.lila) || (before.dz && !after.dz);
      if (before.lila && !after.lila) exit(E.lila, { opacity: 0, scale: '.94', translate: '0px 6px' }, 200);
      if (before.dz && !after.dz) exit(E.dz, { opacity: 0, scale: '.95', translate: '-50% 8px' }, 200);
      /* The halo goes with whoever stood in it. NOT the ring: its opacity is a
         CSS transition already (0 / 1 while she writes / .55 on the result),
         and a running transition outranks a script animation in the cascade —
         the two would fight over one property. */
      if (swap) exit(E.halo, { opacity: 0 }, 200);
      if (before.work && !after.work) exit(E.work, { opacity: 0, translate: '0px 8px' }, 180);
      if (before.show && !after.show) {
        exit(E.showHead, { opacity: 0, translate: '0px -8px' }, 170);
        exit(E.show, { opacity: 0, translate: '0px -8px' }, 170);
        freeze(E.show);
        freeze(E.showHead);
      }
      if (before.paper && !after.paper && !morphToPanel) { root.classList.add('lq-mo-paper-out'); wait = Math.max(wait, 220); }

      begin('change', apply, anims, inert, wait, function (j) {
        /* FIRST — where the pieces that stay are standing now. */
        var keepLila = before.lila && after.lila;
        var r0 = {
          lila: keepLila ? rect(E.lila) : null,
          panel: before.panel && (after.panel || morphToPaper) ? rect(E.panel) : null,
          paper: morphToPanel ? paperRect() : null,
          idrow: before.idrow && after.idrow ? rect(E.idrow) : null
        };
        var role0 = E.role ? E.role.textContent : '';

        swapIn(j, apply);
        root.classList.remove('lq-mo-paper-out');

        var list = j.anims;
        var end = 0;
        function add(a, total) { if (a) { list.push(a); end = Math.max(end, total); } }

        /* LAST, INVERT, PLAY — she glides from where she stood. Anchored on
           her FEET (bottom centre): she stands on a floor, and a scale about
           any other point would lift her off her own ground shadow. */
        if (r0.lila && shown(E.lila) && E.portrait) {
          var r1 = rect(E.lila);
          var s = r1.height ? r0.lila.height / r1.height : 1;
          var dx = (r0.lila.left + r0.lila.width / 2) - (r1.left + r1.width / 2);
          var dy = r0.lila.bottom - r1.bottom;
          if (Math.abs(dx) > 2 || Math.abs(dy) > 2 || Math.abs(1 - s) > .02) {
            var p = rect(E.portrait);
            var origin = px((r1.left + r1.width / 2) - p.left) + ' ' + px(r1.bottom - p.top);
            add(E.portrait.animate([
              { transformOrigin: origin, translate: px(dx) + ' ' + px(dy), scale: s.toFixed(4) },
              { transformOrigin: origin, translate: '0px 0px', scale: '1' }
            ], { duration: 600, easing: GLIDE }), 600);
          }
        }
        if (r0.panel && after.panel && shown(E.panel)) add(glide(E.panel, r0.panel, rect(E.panel), 520), 520);
        if (r0.idrow && shown(E.idrow)) add(glide(E.idrow, r0.idrow, rect(E.idrow), 520, 'y'), 520);

        /* THE MORPH. Its words are held back until the sheet has nearly
           settled: they ride the sheet's scale, and text squeezed 15% sideways
           reads as a glitch, not as paper. */
        var wordsAt = 40;
        if (morphToPanel && r0.paper && shown(E.panel)) {
          var b = rect(E.panel);
          add(E.panel.animate([
            { transformOrigin: '0 0', translate: px(r0.paper.left - b.left) + ' ' + px(r0.paper.top - b.top),
              scale: (r0.paper.width / b.width).toFixed(4) + ' ' + (r0.paper.height / b.height).toFixed(4) },
            { transformOrigin: '0 0', translate: '0px 0px', scale: '1 1' }
          ], { duration: 520, easing: GLIDE }), 520);
          wordsAt = 220;
        }
        if (morphToPaper && r0.panel && E.stage) {
          var q = paperRect();
          root.style.setProperty('--lq-mo-x', px(r0.panel.left - q.left));
          root.style.setProperty('--lq-mo-y', px(r0.panel.top - q.top));
          root.style.setProperty('--lq-mo-sx', (r0.panel.width / q.width).toFixed(4));
          root.style.setProperty('--lq-mo-sy', (r0.panel.height / q.height).toFixed(4));
          root.classList.add('lq-mo-paper-morph');
          end = Math.max(end, 560);
          wordsAt = 200;
        }

        /* ENTER — whatever was not on screen before. */
        var panelIn = !before.panel && !morphToPanel && shown(E.panel);
        if (panelIn) { add(enterAnim(E.panel, { opacity: 0, translate: '0px 26px', scale: '.975' }, 400), 400); wordsAt = 120; }
        var view = root.querySelector('[data-lq-view="' + to + '"]');
        if (shown(view)) {
          hush(view);
          var w = write(lines(view), wordsAt, 0);
          list.push.apply(list, w.anims);
          end = Math.max(end, w.end);
        }
        if (!before.lila && shown(E.lila)) add(enterAnim(E.lila, { opacity: 0, translate: '0px 16px', scale: '.96' }, 460, 60), 520);
        if (!before.dz && shown(E.dz)) add(enterAnim(E.dz, { opacity: 0, translate: '-50% 22px', scale: '1.03' }, 520, 60), 580);
        if (swap) add(enterAnim(E.halo, { opacity: 0 }, 480, 40), 520);
        if (!before.idrow && shown(E.idrow)) add(enterAnim(E.idrow, { opacity: 0, translate: '0px 8px' }, 340, 200), 540);
        else if (E.role && shown(E.role) && E.role.textContent !== role0) add(enterAnim(E.role, { opacity: 0 }, 300, 40), 340);
        if (!before.work && shown(E.work)) {
          var ww = write(kids(E.work).map(function (k) { return [k, 'line']; }), 220, 0);
          list.push.apply(list, ww.anims);
          end = Math.max(end, ww.end);
        }
        if (!before.show && shown(E.show)) {
          var parts = kids(E.showHead).concat(kids(E.show)).filter(function (k) {
            return shown(k) && !k.matches('.lq-show-lead');
          }).map(function (k) { return [k, 'line']; });
          var ws = write(parts, morphToPaper ? wordsAt : 100, 0);
          list.push.apply(list, ws.anims);
          end = Math.max(end, ws.end);
        }
        if (after.paper && !before.paper && !morphToPaper) { root.classList.add('lq-mo-paper-in'); end = Math.max(end, 480); }
        settle(j, end);
      });
    }

    /* ── One question to the next: a page turned in the direction you go ── */
    function turn(dir, apply, current) {
      flush();
      if (!live()) { apply(); return; }
      var old = current();
      var anims = [];
      if (old) anims.push(exitAnim(old, { opacity: 0, translate: px(-18 * dir) + ' 0px' }, 130));
      begin('turn', apply, anims, old ? [old] : [], 130, function (j) {
        swapIn(j, apply);
        var pane = current();
        if (pane) hush(pane.closest('.lq-view'));
        var w = write(pane ? lines({ children: [pane] }) : [], 0, dir);
        j.anims = w.anims;
        if (E.stepLabel) j.anims.push(enterAnim(E.stepLabel, { opacity: 0 }, 260));
        settle(j, w.end);
      });
      if (old) old.inert = true;
    }

    /* ── One designer to the next on the letter ─────────────────────────── */
    function page(dir, apply, parts) {
      flush();
      if (!live()) { apply(); return; }
      var anims = [];
      parts().forEach(function (el) {
        if (!shown(el)) return;
        if (el === E.dz) anims.push(exitAnim(el, { opacity: 0, translate: 'calc(-50% ' + (dir > 0 ? '- ' : '+ ') + '24px) 0px' }, 170));
        else anims.push(exitAnim(el, { opacity: 0, translate: px(-12 * dir) + ' 0px' }, 150));
      });
      begin('page', apply, anims, [], 170, function (j) {
        var src = E.dz ? E.dz.getAttribute('src') : '';
        j.applied = true;
        apply();
        /* The outgoing frames are KEPT until the entrances start, so a sprite
           still decoding never shows her half-arrived and then vanishing. */
        function go() {
          if (job !== j) return;
          var outgoing = j.anims;
          j.anims = [];
          var t = 40;
          var end = 0;
          parts().forEach(function (el) {
            if (!shown(el)) return;
            var a;
            if (el === E.dz) a = enterAnim(el, { opacity: 0, translate: 'calc(-50% ' + (dir > 0 ? '+ ' : '- ') + '24px) 0px' }, 420, 0);
            else { a = enterAnim(el, { opacity: 0, translate: px(14 * dir) + ' 0px' }, 380, t); t += 50; }
            j.anims.push(a);
            end = Math.max(end, t + 420);
          });
          outgoing.forEach(stop);
          settle(j, end);
        }
        var img = E.dz;
        if (img && img.getAttribute('src') !== src && !img.complete && typeof img.decode === 'function') {
          var done = false;
          var once = function () { if (!done) { done = true; go(); } };
          img.decode().then(once, once);
          window.setTimeout(once, 320);
        } else go();
      });
    }

    return {
      change: change,
      turn: turn,
      page: page,
      flush: flush,
      /* A page is still lifting away — Siguiente/Atrás must not fire again,
         or a double tap would skip a question nobody answered. */
      turning: function () { return !!job && job.kind === 'turn' && !job.applied; }
    };
  }

  /* ── LOCAL DEMO BACKEND ──────────────────────────────────────────────────
     `?lq_mock=1` on a local `theme dev` host ONLY: the quiz's two endpoints
     answer from canned replies, so the whole flow — sheet, conversation,
     the letter being written, the result, the pause — can be walked through
     on the preview to judge the transitions. The real backend allow-lists
     giftofkaw.com, so without this the preview stops at the chat with a CORS
     error, and a reading spent on the live site costs the visitor's 48 hours.
     🚨 Same gate as devPreview(): on the shop's own hostname this returns on
     its first line and the real `fetch` is never touched. The sample letter
     is the dev bar's `GOK_LILA_QUIZ_PREVIEW`; `lq_n` picks one to three girls. */
  function installDevMock(cfg) {
    if (!/^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$/.test(location.hostname)) return;
    var params = new URLSearchParams(location.search);
    if (params.get('lq_mock') !== '1' || window.__lqDevMock || typeof window.fetch !== 'function') return;
    window.__lqDevMock = true;
    var realFetch = window.fetch.bind(window);
    var answered = 0;
    function later(body, ms) {
      return new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve(new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json' } }));
        }, ms);
      });
    }
    window.fetch = function (input, options) {
      var url = String((input && input.url) || input);
      var sample = window.GOK_LILA_QUIZ_PREVIEW || {};
      if (cfg.matchEndpoint && url.indexOf(cfg.matchEndpoint) === 0) {
        var n = Math.min(3, Math.max(1, parseInt(params.get('lq_n'), 10) || 2));
        return later({
          success: true,
          presentation: (sample.presentations || [])[n - 1] || '',
          matches: (sample.matches || []).slice(0, n),
          retry_after: sample.retry_after || 172800
        }, 4200);
      }
      if (cfg.quizEndpoint && url.indexOf(cfg.quizEndpoint) === 0) {
        if (!options || String(options.method || 'GET').toUpperCase() === 'GET') {
          return later({ messages_remaining: 8 - answered, messages_max: 8 }, 120);
        }
        var body = {};
        try { body = JSON.parse(options.body || '{}'); } catch (error) {}
        if (body.action === 'start') {
          return later({ success: true, ready: false, messages_remaining: 8,
            reply: 'Hola 💜 Me alegra que estés aquí. Antes de nada: ¿qué te hizo elegir ese mundo?' }, 1100);
        }
        answered += 1;
        return later({ success: true, ready: true, messages_remaining: 8 - answered,
          reply: 'Te entiendo. Cuando dices eso, ¿cómo te gustaría sentirte con ella puesta?' }, 1300);
      }
      return realFetch(input, options);
    };
  }

  /* ══ RESULT SEALS ════════════════════════════════════════════════════════
     One wax seal per designer, stamped with her own emblem. The blob is the
     same irregular pour the frame corners use, so a result letter is visibly
     sealed by the same hand that wrote the page. */
  var SEAL_BLOB = 'M26 8.6c4.2-1.6 8.8.4 10.9 4 2 3.4 6.6 3.2 8.7 6.5 2.2 3.4 1 8-2 10.4-2.9 2.3-2.9 6.9-6.1 8.9-3.3 2-7.8 1-10.3-1.8-2.4-2.7-6.9-2.2-9.6-4.7-2.8-2.6-3.2-7.3-1-10.4 2.1-3 .8-7.4 3.4-10 1.6-1.6 3.8-2.3 6-2.9z';
  var EMBLEMS = {
    rosa:
      '<circle cx="29" cy="29" r="2.6"/>' +
      '<path d="M29 23.4a5.6 5.6 0 0 1 5.6 5.6 5.6 5.6 0 0 1-5.6 5.6 5.6 5.6 0 0 1-5.6-5.6"/>' +
      '<path d="M29 20.2a8.8 8.8 0 0 1 8.8 8.8"/>',
    akira:
      '<rect x="24.4" y="24.4" width="9.2" height="9.2" rx="1.5"/>' +
      '<path d="M29 24.4v-4.2M29 33.6v4.2M24.4 29h-4.2M33.6 29h4.2"/>' +
      '<circle cx="29" cy="29" r="1.4"/>',
    azura:
      '<path d="M29 19.8V38.2M21 24.4l16 9.2M37 24.4l-16 9.2"/>' +
      '<path d="M29 23.4l-2.4-2.4M29 23.4l2.4-2.4M29 34.6l-2.4 2.4M29 34.6l2.4 2.4"/>'
  };



  function init(configKey, cfg) {
    var root = document.getElementById(cfg.rootId);
    if (!root || root.dataset.lqInitialized === 'true') return;
    root.dataset.lqInitialized = 'true';
    installDevMock(cfg);

    var $ = function (selector) { return root.querySelector(selector); };
    var $$ = function (selector) { return Array.prototype.slice.call(root.querySelectorAll(selector)); };
    var storeKey = 'gokLilaQuiz:v2';
    var storeTTL = 48 * 60 * 60 * 1000;
    var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    /* Read live rather than cached: the theme editor can flip `enable_haptics`
       without a reload, and a cached boolean would keep buzzing after it is off.
       🚨 NOT gated on `reduce` -- prefers-reduced-motion is about MOTION, and
       silently disabling touch feedback for people who set it would remove an
       accessibility affordance in the name of an unrelated one. */
    function tapHaptic(pattern) {
      if (!COARSE || typeof navigator.vibrate !== 'function' || !pageActivated()) return;
      if (root.getAttribute('data-haptics') !== 'on') return;
      try { navigator.vibrate(pattern || 10); } catch (error) {}
    }

    var els = {
      views: {},
      messages: $('[data-lq-messages]'),
      typing: $('[data-lq-typing]'),
      input: $('[data-lq-input]'),
      send: $('[data-lq-send]'),
      start: $('[data-lq-start]'),
      finish: $('[data-lq-finish]'),
      counter: $('[data-lq-counter]'),
      allowance: $('[data-lq-allowance]'),
      stageNote: $('[data-lq-stage-note]'),
      stageRole: $('[data-lq-stage-role]'),
      /* The sealing readout moved out of the sheet and onto the stage — the
         old `[data-lq-matching-copy]` paragraph no longer exists. */
      workText: $('[data-lq-work-text]'),
      workRail: $('[data-lq-work-rail]'),
      /* The showcase. `root` and `stage` are held because the designer's
         colour tokens and the "she has art" class hang off elements rather
         than off any one node inside it. */
      root: root,
      stage: $('.lq-stage'),
      designerFig: $('[data-lq-designer-fig]'),
      showName: $('[data-lq-show-name]'),
      showReason: $('[data-lq-show-reason]'),
      showCta: $('[data-lq-show-cta]'),
      showCount: $('[data-lq-show-count]'),
      showNav: $('[data-lq-show-nav]'),
      showDots: $('[data-lq-show-dots]'),
      showPrev: $('[data-lq-show-prev]'),
      showNext: $('[data-lq-show-next]'),
      presentation: $('[data-lq-presentation]'),
      /* Her opening, folded into the result screen's own trigger. Same dialog
         as the two note buttons — a third caller of openPop(), not a second
         popup. */
      opinion: $('[data-lq-opinion]'),
      showLock: $('[data-lq-show-lock]'),
      backResults: $('[data-lq-back-results]'),
      sent: $('[data-lq-sent]'),
      retry: $('[data-lq-retry]'),
      error: $('[data-lq-error]'),
      hours: $('[data-lq-hours]'),
      minutes: $('[data-lq-minutes]'),
      seconds: $('[data-lq-seconds]'),
      arena: $('[data-lq-arena]'),
      quiz: root.querySelector('.lq-quiz'),
      ban: $('[data-lq-ban]'),
      banIcon: $('[data-lq-ban-icon]'),
      banVerdict: $('[data-lq-ban-verdict]'),
      banTime: $('[data-lq-ban-time]'),
      fig: $('[data-lq-fig]'),
      say: $('[data-lq-say]'),
      sayText: $('[data-lq-say-text]'),
      note: $('[data-lq-note]'),
      /* The masthead's envelope trigger and the paragraph it speaks for. Both
         are absent when the `intro` setting is blank — the section does not
         render a button that would open an empty card. */
      leadNote: $('[data-lq-lead-note]'),
      leadText: $('[data-lq-lead-text]'),
      popLink: $('[data-lq-pop-link]'),
      colorMark: $('[data-lq-colormark]'),
      colorInfo: $('[data-lq-colorinfo]'),
      pop: $('[data-lq-pop]'),
      popTitle: $('[data-lq-pop-title]'),
      popText: $('[data-lq-pop-text]'),
      /* The card's second paragraph. Always filled; the STYLESHEET decides
         whether it is read (phone only — desktop shows the reason inline on
         the sheet beside her). */
      popExtra: $('[data-lq-pop-extra]'),
      /* The answer list's container. One dialog, five callers — this is the
         only one that fills it, and openPop() hides it for the other four. */
      popList: $('[data-lq-pop-list]'),
      popClose: $('[data-lq-pop-close]'),
      /* Two triggers, one class, one handler: the sheet's progress row and the
         chat's counter row. Held as a LIST for the same reason `taps` is —
         a third home is a section edit, not a JS edit. */
      picks: $$('[data-lq-picks]'),
      /* The "there is more below" fade. ONE element for every scrolling view,
         because it lives on the panel rather than inside any of them. */
      more: $('[data-lq-more]'),
      /* The pre-quiz sheet. `taps` is a NodeList rather than one handle per
         control: every tile carries `data-lq-tap="field:id"`, so one delegate
         handles all 26 and adding a question needs no JS change at all. */
      taps: $$('[data-lq-tap]'),
      steps: $$('[data-lq-step]'),
      dots: $('[data-lq-dots]'),
      stepLabel: $('[data-lq-steplabel]'),
      navHint: $('[data-lq-navhint]'),
      sheetBack: $('[data-lq-sheet-back]'),
      sheetNext: $('[data-lq-sheet-next]'),
      free: $('[data-lq-free]'),
      freeCount: $('[data-lq-free-count]')
    };

    $$('[data-lq-view]').forEach(function (view) {
      els.views[view.getAttribute('data-lq-view')] = view;
    });

    var poses = setupPoses(root, cfg, reduce);
    /* `lqOffScreen` / `lqTabHidden` are declared further down (the off-screen
       block); read through a closure, so they are only consulted once set. */
    var motion = setupMotion(root, reduce, function () { return lqOffScreen || lqTabHidden; },
      function () { syncScrollCue(); });

    /* Which drawing belongs to which screen. The reactions borrow her back
       for a moment and restorePose() returns her to this.

       🚨 `locked` wears the RESULT pose, and there is deliberately no `locked`
       pose group — her sisters settled this exact question and Lila was the
       odd one out. `character-quiz.liquid:2311` maps `locked: "found"`, their
       after-the-quiz pose, because the cooldown screen IS after the quiz: the
       finish flow keeps the result stage, so she holds the result pose through
       the countdown rather than changing drawing for a screen that is only the
       same moment, later. Giving her a pose of her own also re-opened a split
       they had closed — the same screen showing two different chibis depending
       on how you arrived at it. */
    var VIEW_POSE = {
      intro: 'intro',
      sheet: 'intro',
      chat: 'idle',
      sealing: 'thinking',
      results: 'result',
      locked: 'result',
      error: 'idle'
    };

    var state = {
      history: [],
      messagesMax: Number(cfg.messagesMax) || 8,
      remaining: Number(cfg.messagesMax) || 8,
      ready: false,
      busy: false,
      started: false,
      matches: [],
      presentation: '',
      retryAfter: null,
      lockEndsAt: null,
      lockTimer: null,
      fingerprint: null,
      banned: false,
      banTimer: null,
      /* What the sheet collected, or null for the skip path. Sent ONCE, on
         `start`, because it describes the person rather than the turn. */
      prequiz: null,
      /* Which question of the sheet is on screen. Not persisted: the sheet is a
         sixty-second thing, and restoring someone to question four of a form
         they half-remember is worse than letting them start it again. */
      sheetStep: 0,
      sheetDraft: null,
      view: 'intro'
    };

    function restorePose() { poses.set(VIEW_POSE[state.view] || 'idle', true); }

    var playCfg = cfg.play || {};
    var player = setupPlay({
      fig: els.fig,
      arena: els.arena,
      poses: poses,
      play: playCfg,
      bubble: els.say,
      bubbleText: els.sayText,
      restorePose: restorePose,
      reduce: reduce,
      haptics: root.getAttribute('data-haptics') === 'on' && playCfg.haptics !== false,
      /* 🚨 NOT `true` any more. On the result screen Lila's figure is
         `display:none` — the designer is standing in her arena — so a poke or
         a drag there would be aimed at a hidden element: her pose would swap,
         her bubble would appear beside a chibi that is not her, and the
         refusal would block input on a screen she is not even on. */
      canPlay: function () { return state.view !== 'results'; }
    });

    function save() {
      try {
        localStorage.setItem(storeKey, JSON.stringify({
          v: 2,
          ts: Date.now(),
          history: state.history.slice(-40),
          remaining: state.remaining,
          ready: state.ready,
          started: state.started,
          matches: state.matches,
          presentation: state.presentation,
          lockEndsAt: state.lockEndsAt,
          prequiz: state.prequiz,
          /* The sheet IN PROGRESS. `prequiz` above is only written on the way OUT
             of the sheet, so it cannot stand in for a draft -- a reload halfway
             through used to lose every tap. Her sisters persist step + answers +
             choices for exactly this reason. */
          sheetStep: state.sheetStep,
          sheetDraft: state.view === 'sheet' ? collectSheet() : state.sheetDraft,
          view: state.view
        }));
      } catch (error) {
        /* Storage is a convenience. The server's limits remain authoritative. */
      }
    }

    function restore() {
      try {
        var parsed = JSON.parse(localStorage.getItem(storeKey) || 'null');
        if (!parsed || parsed.v !== 2 || Date.now() - Number(parsed.ts || 0) > storeTTL) return false;
        state.history = Array.isArray(parsed.history) ? parsed.history.slice(-40) : [];
        state.remaining = Number.isFinite(Number(parsed.remaining)) ? Number(parsed.remaining) : state.messagesMax;
        state.ready = parsed.ready === true;
        state.started = parsed.started === true;
        state.matches = Array.isArray(parsed.matches) ? parsed.matches : [];
        state.presentation = typeof parsed.presentation === 'string' ? parsed.presentation : '';
        state.lockEndsAt = Number(parsed.lockEndsAt) || null;
        state.prequiz = parsed.prequiz && typeof parsed.prequiz === 'object' ? parsed.prequiz : null;
        state.sheetStep = Number.isFinite(Number(parsed.sheetStep)) ? Number(parsed.sheetStep) : 0;
        state.sheetDraft = parsed.sheetDraft && typeof parsed.sheetDraft === 'object' ? parsed.sheetDraft : null;
        state.view = typeof parsed.view === 'string' ? parsed.view : state.view;
        return true;
      } catch (error) {
        return false;
      }
    }

    var ROLE = {
      intro: cfg.copy.roleIntro,
      sheet: cfg.copy.roleIntro,
      chat: cfg.copy.roleChat,
      sealing: cfg.copy.roleThinking,
      results: cfg.copy.roleIntro,
      locked: cfg.copy.roleLocked,
      error: cfg.copy.roleIntro
    };
    var NOTE = {
      intro: cfg.copy.stageIntro,
      sheet: cfg.copy.stageIntro,
      chat: cfg.copy.stageIntro,
      sealing: cfg.copy.stageThinking,
      results: cfg.copy.stageResult,
      locked: cfg.copy.stageResult,
      error: cfg.copy.stageIntro
    };

    /* 🔑 TWO HALVES since the transitions (2026-10-02, setupMotion). The LOGIC
       half runs now: `state.view`, the status-line backstop, her press, the
       save — everything the rest of this file reads. The SCREEN half (the
       state attribute, which view is shown, her pose, the plate) is handed to
       motion.change(), which runs it at once when nothing should move, or
       after the outgoing screen has lifted away (≤280ms) when it should.
       `after` runs with the screen half — the only place a caller may touch
       the NEW screen (focus, measure). */
    function setView(name, after) {
      var from = state.view;
      state.view = name;
      /* 🚨 BACKSTOP for the rotating status line. finishInterview stops it on
         the way out, but an error takeover or a rejected request can leave the
         sealing screen without passing through there — and an interval writing
         into a hidden element would then run for the life of the page. Her
         sisters keep the identical guard (`quiz.js:1469`).
         Safe to sit above beginWorking's own setStageText: this fires on the
         setView INSIDE beginWorking, which happens first. */
      if (name !== 'sealing') stopWorkLines();
      /* Her press dies with the screen it started on. */
      if (player) player.cancel();
      motion.change(from, name, function () {
        root.setAttribute('data-lq-state', name);
        Object.keys(els.views).forEach(function (key) {
          var active = key === name;
          els.views[key].hidden = !active;
          els.views[key].classList.toggle('is-active', active);
        });
        restorePose();
        if (els.stageRole && ROLE[name]) els.stageRole.textContent = ROLE[name];
        if (els.stageNote && NOTE[name]) els.stageNote.textContent = NOTE[name];
        /* Here rather than in each caller: picksSource() branches on state.view,
           so every view change can change both the count and whether the button
           is shown at all. This is also what covers restore() and restart(),
           which reach a view through here and nowhere else. */
        updatePicksCount();
        /* The active view changed, so both "does it scroll" and "how far" did. */
        syncScrollCue();
        /* A reply that landed while the transcript was still hidden scrolled a
           box with no height; now it has one. */
        if (name === 'chat') scrollMessages();
        if (after) after();
      });
      save();
    }

    function updateBudget() {
      state.remaining = Math.max(0, Math.min(state.messagesMax, Number(state.remaining) || 0));
      var word = cfg.copy.counter || 'respuestas';
      if (els.allowance) els.allowance.textContent = String(state.remaining);
      if (els.counter) {
        els.counter.textContent = state.remaining + ' de ' + state.messagesMax + ' ' + word + ' disponibles';
      }
      /* 🚨 A ban outranks the budget. This function re-derives every control's
         disabled state from `state`, so without this the next refresh after a
         ban would quietly hand the input back. applyBan disables them once;
         this keeps them that way. */
      if (state.banned) return;
      if (els.finish) {
        els.finish.disabled = !state.ready || state.busy;
        els.finish.setAttribute('aria-disabled', els.finish.disabled ? 'true' : 'false');
      }
      if (els.input) els.input.disabled = state.busy || state.remaining <= 0;
      if (els.send) els.send.disabled = state.busy || state.remaining <= 0;
    }

    function scrollMessages() {
      if (!els.messages) return;
      requestAnimationFrame(function () {
        els.messages.scrollTop = els.messages.scrollHeight;
      });
    }

    /* ── THE TRANSCRIPT'S OVERSCROLL FOLLOWS REALITY ──────────────────────
       `contain` stops a flick that reaches the last message from carrying on
       into the page, which a long transcript genuinely wants. But while there
       is NOTHING to scroll it blocks the chaining too, and a drag over the chat
       does nothing at all — the page just feels stuck, which is what happens
       with only her opening question on screen (measured scrollHeight ===
       clientHeight, still swallowing the gesture).

       So the value follows reality: `contain` only while scrollable, `auto`
       otherwise. rAF-throttled because every added message mutates this and
       reading scrollHeight forces layout. Straight from `quiz.js:1873`. */
    var overscrollQueued = false;
    function syncMessagesOverscroll() {
      if (!els.messages || overscrollQueued) return;
      overscrollQueued = true;
      requestAnimationFrame(function () {
        overscrollQueued = false;
        if (!els.messages) return;
        var scrollable = els.messages.scrollHeight - els.messages.clientHeight > 1;
        els.messages.style.overscrollBehavior = scrollable ? 'contain' : 'auto';
      });
    }
    if (els.messages) {
      /* Covers every path that changes the height — a message added, the typing
         bubble, a restart clearing it, and rotation. */
      if (window.MutationObserver) {
        try {
          new MutationObserver(syncMessagesOverscroll)
            .observe(els.messages, { childList: true, subtree: true, characterData: true });
        } catch (error) {}
      }
      if (window.ResizeObserver) {
        try { new ResizeObserver(syncMessagesOverscroll).observe(els.messages); } catch (error) {}
      }
      syncMessagesOverscroll();
    }

    /* ── "THERE IS MORE BELOW" ─────────────────────────────────────────────
       The sheet's longest questions do not fit a phone — six options on `freno`,
       seven on `paleta`, about three visible — and a touch device paints no
       scrollbar until the visitor has already scrolled, which is the one
       moment the hint is no longer needed.

       The CHAT is excluded on purpose: it is the one view whose scrolling is
       already obvious, because the transcript moves under a composer that
       stays put, and it manages its own inner scroller. */
    function activeScroller() {
      var view = els.views[state.view];
      if (!view || view.hidden) return null;
      if (view.classList.contains('lq-view--chat')) return null;
      return view;
    }

    var cueQueued = false;
    function syncScrollCue() {
      if (!els.more || cueQueued) return;
      cueQueued = true;
      requestAnimationFrame(function () {
        cueQueued = false;
        if (!els.more) return;
        var view = activeScroller();
        var on = false;
        if (view) {
          var max = view.scrollHeight - view.clientHeight;
          /* 4px rather than 0: sub-pixel layout routinely leaves a pixel or two
             of phantom scroll on a pane that is visually full, and a cue that
             points at nothing is worse than none. The same slack decides "at
             the bottom", so the fade clears instead of hovering forever one
             rounding error short of the end.
             A view inside a `display:none` panel reports 0/0 and lands on
             false by itself — results and sealing need no special case. */
          on = max > 4 && (max - view.scrollTop) > 4;
          /* 🔑 A PICKED OPTION OUTRANKS THE CUE. The fade lands on the last
             visible tile, and a tile that has just been chosen is the one thing
             on screen that must read as chosen — its highlight going muddy
             under a gradient looks like a rendering fault rather than a hint.

             Scoped to the sheet, and to THIS question: moving to the next one
             brings the cue straight back, and un-picking restores it too, since
             the tap handler re-runs this either way. */
          if (on && view === els.views.sheet && stepHasSelection(state.sheetStep)) on = false;
        }
        els.more.classList.toggle('is-on', on);
      });
    }

    Object.keys(els.views).forEach(function (key) {
      if (key === 'chat' || !els.views[key]) return;
      els.views[key].addEventListener('scroll', syncScrollCue, { passive: true });
    });
    /* 🚨 A ResizeObserver on the VIEW is not enough and it is worth saying why:
       it watches the border box, which does not move when the CONTENT inside a
       fixed-height scroller changes. Stepping from a 4-option question to a
       7-option one is exactly that case. So the observer covers geometry
       (rotation, the address bar, a font landing) and renderSheetStep/setView
       call this directly for content. */
    if (window.ResizeObserver) {
      try {
        var cueRO = new ResizeObserver(syncScrollCue);
        if (els.quiz) cueRO.observe(els.quiz);
        Object.keys(els.views).forEach(function (key) {
          if (key !== 'chat' && els.views[key]) cueRO.observe(els.views[key]);
        });
      } catch (error) {}
    }
    window.addEventListener('resize', syncScrollCue);

    /* Lila's writing style uses sparse **bold** as a warm highlighter. Support
       that one device and nothing else; everything else stays text. */
    /* 🚨 A SINGLE ASTERISK PAIR IS SWALLOWED, NOT RENDERED — measured live
       2026-08-24, when she wrote «algo *para ti*» and the visitor would have seen
       the asterisks. Only `**` was ever parsed, so anything else arrived as text.

       🔑 SWALLOWED RATHER THAN TURNED INTO <em>, on purpose, and the reason is not
       tidiness. Bold is the ONLY emphasis her writing is designed around — «muy
       escasa» in the interview, exactly one idea in the result — so quietly giving
       her italics invents a second register nobody designed. Worse, the judge
       prompt bans stage directions in exactly this shape («*Lila sonríe*»);
       rendering those as elegant italics would make a banned move look deliberate,
       where dropping the marks leaves plain words that read as a slip.

       The prompt already tells her a lone asterisk shows up as an asterisk. This is
       the net under that rule, because a rule can fail and this cannot.

       ⚠️ ONLY A REAL PAIR. Standard markdown adjacency — no space after the opening
       mark, none before the closing one — so a lone `*` survives and so does
       arithmetic like «3 * 4 y 5 * 6», which a laxer pattern read as one emphasis
       span and quietly ate. Caught by the stub probe, not by reading it.
       `**` is tested FIRST in the alternation, or bold would be eaten by the
       single-asterisk branch. */
    function setLilaText(element, content) {
      element.textContent = '';
      String(content || '').split(/(\*\*[^*]+\*\*|\*[^*\s](?:[^*\n]*[^*\s])?\*)/g).forEach(function (part) {
        if (!part) return;
        if (part.slice(0, 2) === '**' && part.slice(-2) === '**') {
          var strong = document.createElement('strong');
          strong.textContent = part.slice(2, -2);
          element.appendChild(strong);
        } else if (part.length > 2 && part.charAt(0) === '*' && part.charAt(part.length - 1) === '*') {
          element.appendChild(document.createTextNode(part.slice(1, -1)));
        } else {
          element.appendChild(document.createTextNode(part));
        }
      });
    }

    /* ── HER NOTE ──────────────────────────────────────────────────────────
       Her aside used to run as four lines directly under the portrait, which
       pushed her up the stage and ate the room she is played with in. It sits
       behind a sealed note now — the same height her sisters bought back by
       moving their welcome line into a dialog, and the same architecture, but
       her object rather than theirs.

       The copy is still authored in exactly ONE place: the hidden <p> the
       section renders. This reads it at click time, so the note keeps
       following the view the way it always did (intro / writing / result).
       🚨 It is parsed HERE rather than on init — the source keeps its literal
       `**` so the emphasis survives the trip into the dialog. */
    var noteReturn = null;                 /* who gets focus back on close */
    var scrollLockY = 0, scrollLocked = false;

    function lockScroll() {
      if (scrollLocked) return;
      scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
      var b = document.body;
      b.style.position = 'fixed';
      b.style.top = (-scrollLockY) + 'px';
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
      b.style.overflow = 'hidden';
      scrollLocked = true;
    }
    function unlockScroll() {
      if (!scrollLocked) return;
      var b = document.body;
      b.style.position = ''; b.style.top = ''; b.style.left = '';
      b.style.right = ''; b.style.width = ''; b.style.overflow = '';
      /* Restoring the offset must not animate — a themed `scroll-behavior:smooth`
         would turn closing the note into a visible slide back up the page. */
      var h = document.documentElement, prev = h.style.scrollBehavior;
      h.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollLockY);
      h.style.scrollBehavior = prev;
      scrollLocked = false;
    }

    function noteIsOpen() { return !!(els.pop && els.pop.classList.contains('is-open')); }

    /* ONE dialog, TWO triggers: the stage's sealed note (her words about the
       conversation) and the masthead's envelope (what the quiz is). Each names
       its own source paragraph and carries its own visible label, which becomes
       the card's heading — the same contract `.cq-titlenote` uses on her
       sisters, and the reason both labels live in the markup rather than in an
       aria-label: an earlier version of theirs opened untitled once the markup
       was restructured.

       🚨 The source paragraphs are read as `.textContent` and parsed here, so
       neither may carry `data-lq-md` — that pass would have already turned her
       `**bold**` into <strong> and the asterisks would be gone by now. */
    /* `source` and `extra` may be an ELEMENT to read, or a raw STRING.

       The string form exists for the result screen and it is not a
       convenience: `.lq-show-lead` has already been through setLilaText(), so
       its `**bold**` is resolved into <strong> and its `.textContent` would
       arrive with the asterisks stripped — her emphasis silently lost. The two
       note buttons keep passing elements, whose sources deliberately carry no
       `data-lq-md` for exactly the same reason. */
    function popSource(value) {
      if (value == null) return '';
      return typeof value === 'string' ? value : String(value.textContent || '');
    }

    function openPop(trigger, source, labelSelector, extra) {
      if (!els.pop || !trigger || !source) return;
      noteReturn = trigger;
      /* Reset the optional CTA on EVERY open, so a caller that wants it has to
         switch it on itself and no other caller can inherit it from last time.
         The answer list is reset here for the same reason and in the same
         breath — one place that clears every optional part of the card. */
      if (els.popLink) els.popLink.hidden = true;
      if (els.popList) els.popList.hidden = true;
      var label = trigger.querySelector(labelSelector);
      if (els.popTitle) els.popTitle.textContent = label ? label.textContent.trim() : 'Lila';
      if (els.popText) setLilaText(els.popText, popSource(source));
      /* Hidden when there is nothing to put in it, so the hairline that
         separates the two paragraphs never draws under an empty one. */
      if (els.popExtra) {
        var extraText = popSource(extra);
        setLilaText(els.popExtra, extraText);
        els.popExtra.hidden = !extraText;
      }
      els.pop.classList.add('is-open');
      lockScroll();
      /* Focus AFTER the freeze. Focusing a control first can make the browser
         scroll it into view, and THAT position is what the unlock would then
         restore to — the page would come back somewhere the visitor never
         scrolled to. */
      if (els.popClose) els.popClose.focus();
    }

    function closeNote() {
      if (!els.pop) return;
      els.pop.classList.remove('is-open');
      unlockScroll();
      /* Drop the rows rather than leave them staged in a closed dialog: the
         sheet changes between two opens, and stale markup sitting behind
         `hidden` is one missed reset away from being shown again. */
      if (els.popList) { els.popList.innerHTML = ''; els.popList.hidden = true; }
      if (noteReturn) noteReturn.focus();
      noteReturn = null;
    }

    /* The palette "?" -- a fourth caller of openPop(), reusing the same dialog,
       close handling, focus return and scroll lock as the two note buttons.
       Replaced six per-swatch descriptions: those were paraphrases of catalogue
       copy, and one of them ("Rojo entero") contradicted it outright. */
    if (els.colorMark && els.colorInfo) {
      els.colorMark.addEventListener('click', function () {
        openPop(els.colorMark, els.colorInfo, '.lq-qmark-l');
        /* The one caller that shows the way out to the worlds. */
        if (els.popLink) els.popLink.hidden = false;
      });
    }

    if (els.note) {
      els.note.addEventListener('click', function () {
        openPop(els.note, els.stageNote, '.lq-note-l');
      });
    }
    if (els.leadNote) {
      els.leadNote.addEventListener('click', function () {
        openPop(els.leadNote, els.leadText, '.lq-titlenote-l');
      });
    }
    /* 🔑 Fed from STATE, not from the DOM — see popSource(). The reason is read
       at click time rather than bound once, because the pager rewrites it on
       every page and the card must show the designer currently on screen. */
    if (els.opinion) {
      els.opinion.addEventListener('click', function () {
        var current = state.matches[showAt];
        openPop(els.opinion, state.presentation, '.lq-opinion-l', current && current.reason);
      });
    }
    /* ── THE ANSWER LIST ──────────────────────────────────────────────────
       Her sisters have carried "Tus elecciones" since the beginning
       (renderPicksSummary, quiz.js:1515) and this quiz never did — which is
       backwards, because THEIRS is three questions on one screen and hers are
       asked ONE AT A TIME (six until 2026-08-24, four since), then buried under a
       conversation. Everything
       already answered is off-screen by design here; this is the way back.

       🔑 IT IS READ-ONLY ONCE THE CONVERSATION STARTS, and that is a data
       constraint rather than a design preference. `prequiz` is posted with
       `action: 'start'` and NEVER again by sendAnswer — but askForMatch does
       send it. So a sheet edited mid-chat would mean the interviewer had seen
       one sheet and the judge was handed a different one, silently. On the
       sheet itself "Atrás" already exists and changes nothing that has left
       the browser.

       The COPY is not duplicated here. Values are read back out of the tiles
       the visitor actually tapped, and the row labels off `data-lq-short` on
       each fieldset — the same rule collectSheet() follows by storing ids and
       never labels. A list that held its own wording could tell someone they
       picked something they never saw. */
    function pickValueLabel(field, id) {
      var tap = root.querySelector('[data-lq-tap="' + field + ':' + id + '"]');
      if (!tap) return '';
      var strong = tap.querySelector('.lq-opt-txt strong');
      return String((strong || tap).textContent || '').trim();
    }

    /* Live DOM while the sheet is open — the visitor is still editing and
       collectSheet() is the truth. The COMMITTED sheet everywhere else, because
       after leaveSheet() the taps may not even be pressed: a page restored into
       the chat view never runs applySheetDraft(). */
    function picksSource() {
      return state.view === 'sheet' ? collectSheet() : state.prequiz;
    }

    function pickedValue(sheet, field) {
      var raw = sheet ? sheet[field] : null;
      if (field === 'libre') return typeof raw === 'string' ? raw.trim() : '';
      if (!raw) return '';
      return (Array.isArray(raw) ? raw : [raw])
        .map(function (id) { return pickValueLabel(field, id); })
        .filter(Boolean)
        .join(' · ');
    }

    function renderPicks() {
      if (!els.popList) return 0;
      var sheet = picksSource();
      var filled = 0;
      els.popList.innerHTML = '';
      els.steps.forEach(function (pane) {
        var field = pane.getAttribute('data-lq-field');
        var text = pickedValue(sheet, field);
        if (text) filled++;
        var row = document.createElement('div');
        row.className = 'lq-prow' + (text ? '' : ' is-empty');
        var label = document.createElement('dt');
        label.className = 'lq-pl';
        label.textContent = pane.getAttribute('data-lq-short') || field;
        var value = document.createElement('dd');
        value.className = 'lq-pv';
        /* textContent, never innerHTML: `libre` is up to 300 characters the
           visitor typed themselves. */
        value.textContent = text || (field === 'libre' ? 'Lo dejaste en blanco' : 'Sin responder');
        row.appendChild(label);
        row.appendChild(value);
        els.popList.appendChild(row);
      });
      return filled;
    }

    function pickedCount() {
      var sheet = picksSource();
      if (!sheet) return 0;
      return els.steps.filter(function (pane) {
        return !!pickedValue(sheet, pane.getAttribute('data-lq-field'));
      }).length;
    }

    /* The count is what makes the closed button worth pressing, and the whole
       label on a phone — the word is display:none there in both homes. */
    function updatePicksCount() {
      if (!els.picks.length) return;
      var n = pickedCount();
      var total = els.steps.length;
      els.picks.forEach(function (button) {
        var badge = button.querySelector('[data-lq-picks-count]');
        if (badge) badge.textContent = String(n);
        var word = button.querySelector('.lq-picks-l');
        button.setAttribute('aria-label',
          (word ? word.textContent.trim() : 'Tus respuestas') + ': ' + n + ' de ' + total);
        /* 🚨 IT IS ALWAYS THERE, SHOWING 0 — it is deliberately NOT hidden at
           zero, and the reason is layout rather than taste.

           This used to hide until the first answer, on the argument that a
           button with nothing behind it "has not earned its place". True of the
           button, false of the ROW: on a phone this is a bordered pill 35px
           tall in a head that is otherwise 18px of text, so it appearing after
           the first tap shoved the question and every option below it down the
           screen — under the visitor's thumb, at the exact moment they were
           reaching for the next one.

           A control that is present from the start and simply counts up costs
           nothing and moves nothing. Opening it at 0 is honest too: a row of
           "Sin responder" per question is a preview of what she is going to ask. */
      });
    }

    els.picks.forEach(function (button) {
      button.addEventListener('click', function () {
        tapHaptic(8);
        renderPicks();
        openPop(button, 'Esto es lo que me contaste antes de empezar. 💜', '.lq-picks-l');
        /* AFTER openPop, which hides it for every caller that does not want it. */
        if (els.popList) els.popList.hidden = false;
      });
    });

    if (els.popClose) els.popClose.addEventListener('click', closeNote);
    /* Backdrop only — a click that started on the card must not close it. */
    if (els.pop) els.pop.addEventListener('click', function (event) {
      if (event.target === els.pop) closeNote();
    });
    document.addEventListener('keydown', function (event) {
      if (noteIsOpen() && (event.key === 'Escape' || event.key === 'Esc')) closeNote();
    });

    function addMessage(role, content, record) {
      if (!content) return;
      var item = document.createElement('div');
      item.className = 'lq-message lq-message--' + (role === 'user' ? 'user' : 'lila');
      var bubble = document.createElement('div');
      bubble.className = 'lq-bubble';
      setLilaText(bubble, content);
      item.appendChild(bubble);
      els.messages.appendChild(item);
      if (record !== false) {
        state.history.push({ role: role === 'user' ? 'user' : 'assistant', content: content });
      }
      scrollMessages();
    }

    function rebuildMessages() {
      els.messages.innerHTML = '';
      state.history.forEach(function (item) {
        addMessage(item.role === 'user' ? 'user' : 'assistant', item.content, false);
      });
    }

    function setTyping(active, label) {
      if (!els.typing) return;
      els.typing.hidden = !active;
      var text = els.typing.querySelector('span');
      if (text && label) text.textContent = label;
      scrollMessages();
    }

    function autoGrow() {
      if (!els.input) return;
      els.input.style.height = 'auto';
      els.input.style.height = Math.min(130, els.input.scrollHeight) + 'px';
    }

    function setBusy(active) {
      state.busy = active;
      updateBudget();
    }

    /* 🔑 SOME ERRORS ARE WRITTEN FOR A PERSON AND SOME ARE NOT.
       `fetch` rejects with a TypeError whose message is the browser's own
       ("Failed to fetch", and localised differently per engine); a JSON parse
       failure gives a SyntaxError. Neither belongs on the page. Only the
       messages this file throws deliberately - which carry the server's own
       Spanish, or a written fallback - are marked `human` and shown.

       Anything unmarked falls through to `undefined`, and showError leaves the
       section's authored `error_copy` exactly as rendered. */
    function humanError(message) {
      var error = new Error(message);
      error.human = true;
      return error;
    }

    /* ── SUSPENDED ────────────────────────────────────────────────────────
       Lila's ban is ONE ban across the whole store: she can set it in the chat
       and it holds here, and she can set it here and it holds there. The server
       is the only authority on it - this just renders what it is told.

       Mirrors her sisters' takeover in assets/quiz.js so the two behave the
       same way, including the icon and verb, which are already hers there. */
    var BAN_ICON = '📋';
    var BAN_VERB = 'suspendido';

    function formatBanTime(seconds) {
      var days = Math.floor(seconds / 86400);
      var hours = Math.floor((seconds % 86400) / 3600);
      if (days > 0 && hours > 0) return days + 'd ' + hours + 'h';
      if (days > 0) return days + (days > 1 ? ' días' : ' día');
      if (hours > 0) return hours + 'h';
      return Math.max(1, Math.floor(seconds / 60)) + 'min';
    }

    function applyBan(seconds) {
      var secs = Number(seconds);
      if (!isFinite(secs) || secs <= 0) return;
      state.banned = true;
      motion.flush();
      if (player) player.cancel();
      window.clearInterval(state.lockTimer);
      state.lockTimer = null;
      window.clearInterval(state.banTimer);
      state.banTimer = null;

      if (els.banIcon) els.banIcon.textContent = BAN_ICON;
      if (els.banVerdict) els.banVerdict.textContent = 'Lila te ha ' + BAN_VERB;

      /* Every control dies with it — nothing behind the takeover stays
         reachable by keyboard, which visibility:hidden alone would not do. */
      [els.input, els.send, els.finish, els.start, els.retry, els.showLock, els.backResults]
        .forEach(function (el) { if (el) el.setAttribute('disabled', 'disabled'); });
      if (els.quiz) els.quiz.classList.add('is-banned');
      if (els.ban) els.ban.hidden = false;

      var unlockAt = Date.now() + Math.ceil(secs) * 1000;
      function tick() {
        var left = Math.max(0, Math.ceil((unlockAt - Date.now()) / 1000));
        if (els.banTime) {
          els.banTime.textContent = left > 0
            ? ((cfg.copy && cfg.copy.banReturnLabel) || 'Vuelve en') + ' ' + formatBanTime(left)
            : 'Ya puedes volver. Recarga la página.';
        }
        if (left <= 0 && state.banTimer) { window.clearInterval(state.banTimer); state.banTimer = null; }
      }
      tick();
      /* Bans run for a day or more — a per-minute tick is plenty. */
      state.banTimer = window.setInterval(tick, 60000);
    }

    /* Every server shape that means "you are out": the 403 on either POST, a
       turn in which she bans, or the ban map on the status read.

       🚨 This is why it exists at all: without it the 403 body's
       `message: 'banned'` falls through the ordinary error path and the page
       prints the literal word "banned" at the visitor. Her sisters carry the
       same warning in assets/quiz.js. */
    function checkBanPayload(res) {
      if (!res) return false;
      if (res.banned && res.ban_expires) { applyBan(res.ban_expires); return true; }
      if (res.message === 'banned' && res.ban_expires) { applyBan(res.ban_expires); return true; }
      if (res.bans && res.bans.lila) { applyBan(res.bans.lila); return true; }
      return false;
    }

    function showError(message) {
      setBusy(false);
      setTyping(false);
      if (els.error && message) els.error.textContent = message;
      setView('error');
    }

    function responseReady(response) {
      state.ready = state.ready || Boolean(response && response.ready === true);
      if (response && Number.isFinite(Number(response.messages_remaining))) {
        state.remaining = Number(response.messages_remaining);
      }
      if (state.remaining <= 0) state.ready = true;
      updateBudget();
    }

    function startConversation() {
      if (state.started) return Promise.resolve();
      state.started = true;
      setBusy(true);
      setTyping(true, 'Lila está preparando su primera pregunta');
      return streamSSE(cfg.quizEndpoint, {
        action: 'start',
        history: [],
        fingerprint: state.fingerprint,
        visitorName: getVisitorName(),
        /* Its own field, never a history entry. A synthesised `user` turn would
           inflate the server's answer count and let a tap sheet produce a full
           verdict with no conversation — the backend asserts this too. */
        prequiz: state.prequiz
      }, function (stage) {
        if (stage === 'responding') setTyping(true, 'Lila está escribiendo');
      }).then(function (response) {
        setBusy(false);
        setTyping(false);
        /* Same two shapes as sendAnswer — see the note there. */
        if (!response || !response.reply) {
          if (checkBanPayload(response)) return;
          throw humanError((response && response.message) || 'No pude iniciar la conversación.');
        }
        if (response.success === false) {
          throw humanError(response.reply || response.message || 'No pude iniciar la conversación.');
        }
        addMessage('assistant', response.reply);
        responseReady(response);
        save();
      }).catch(function (error) {
        state.started = false;
        showError(error && error.human ? error.message : undefined);
      });
    }

    /* Back to the beginning. Clears the LOCAL transcript only: on the real
       backend the messages she has already answered are spent, and the server
       stays the authority on that — so the budget is re-read rather than
       invented. In mock mode there is nothing to re-read, so it resets. */
    function restart() {
      /* A page still turning would land AFTER the reset and save its draft
         back over the cleared storage — finish it first. */
      motion.flush();
      if (player) player.cancel();
      state.history = [];
      state.matches = [];
      state.presentation = '';
      state.ready = false;
      state.started = false;
      state.retryAfter = null;
      state.prequiz = null;
      state.sheetStep = 0;
      state.sheetDraft = null;
      els.taps.forEach(function (button) { press(button, false); });
      if (els.free) { els.free.value = ''; updateFreeCount(); }
      els.taps.forEach(function (button) { press(button, false); });
      if (els.free) els.free.value = '';
      updateFreeCount();
      renderSheetStep();
      state.lockEndsAt = null;
      window.clearInterval(state.lockTimer);
      state.lockTimer = null;
      if (els.messages) els.messages.innerHTML = '';
      if (els.showLock) els.showLock.hidden = true;
      try { localStorage.removeItem(storeKey); } catch (error) {}
      setBusy(false);
      setTyping(false);
      updateBudget();
      setView('intro');
      readStatus();
    }

    /* ── THE PRE-QUIZ SHEET ───────────────────────────────────────
       Which field each tile belongs to, and whether that field takes one tap
       or two, is read off the MARKUP rather than duplicated here. The backend
       owns the labels, the section owns the layout, and this owns neither. */
    /* 🚨 THIS MUST TRACK `multiple` IN PREQUIZ_SHEET (lib/lilaquiz.js), and a
       drift is SILENT. collectSheet posts an array for anything listed here and
       a bare string for anything absent; sanitizePrequiz type-checks each branch
       and DROPS what does not match. So a field listed here that the backend
       calls single-select does not error — the answer simply never arrives, and
       Lila is told they skipped a question they actually answered.

       ⚠️ `paleta` left this list on 2026-08-25, when the six product colourways
       became three designer palettes and picking two stopped meaning anything. */
    var SHEET_MULTI = { freno: 2 };
    /* The tile that means "none of these": tapping it clears the rest of its
       group, and tapping any other clears it. "Nada de eso me frena" plus two
       frenos is a contradiction the visitor never meant. Single-select fields
       need no entry — toggleTap already clears the group on every tap. */
    var SHEET_EXCLUSIVE = { freno: 'nada' };

    function tapParts(button) {
      var raw = (button.getAttribute('data-lq-tap') || '').split(':');
      return { field: raw[0], id: raw[1] };
    }

    function tapsIn(field) {
      return els.taps.filter(function (button) { return tapParts(button).field === field; });
    }

    function pressed(button) { return button.getAttribute('aria-pressed') === 'true'; }
    function press(button, on) { button.setAttribute('aria-pressed', on ? 'true' : 'false'); }

    function toggleTap(button) {
      var part = tapParts(button);
      var limit = SHEET_MULTI[part.field] || 1;
      var group = tapsIn(part.field);
      var turningOn = !pressed(button);

      if (!turningOn) { press(button, false); return; }

      if (limit === 1) {
        group.forEach(function (other) { press(other, other === button); });
        return;
      }

      var exclusive = SHEET_EXCLUSIVE[part.field];
      if (part.id === exclusive) {
        group.forEach(function (other) { press(other, other === button); });
        return;
      }
      group.forEach(function (other) {
        if (tapParts(other).id === exclusive) press(other, false);
      });

      /* At the cap, the OLDEST selection gives way rather than the tap being
         silently ignored. A tile that does nothing when tapped reads as broken. */
      var on = group.filter(pressed);
      if (on.length >= limit) press(on[0], false);
      press(button, true);
    }

    /* Collects ids, never labels: the backend holds its own copy of the copy,
       so the two can never disagree about what the visitor was shown. */
    function collectSheet() {
      var out = {};
      els.taps.forEach(function (button) {
        if (!pressed(button)) return;
        var part = tapParts(button);
        if (SHEET_MULTI[part.field]) {
          out[part.field] = (out[part.field] || []).concat(part.id);
        } else {
          out[part.field] = part.id;
        }
      });
      var libre = els.free ? els.free.value.trim() : '';
      if (libre) out.libre = libre;
      return Object.keys(out).length ? out : null;
    }

    /* The inverse of collectSheet(). The taps are DOM state, so restoring means
       writing `aria-pressed` back rather than assigning a variable. Unknown ids are
       ignored on purpose: the sheet's options can change between two visits, and a
       stale id must not throw away the rest of the draft. */
    function applySheetDraft(draft) {
      if (!draft || typeof draft !== 'object') return;
      els.taps.forEach(function (button) {
        var part = tapParts(button);
        var saved = draft[part.field];
        press(button, Array.isArray(saved) ? saved.indexOf(part.id) !== -1 : saved === part.id);
      });
      if (els.free && typeof draft.libre === 'string') {
        els.free.value = draft.libre;
        updateFreeCount();
      }
    }

    /* Every tap and every step move writes the draft. Saving only on the way out
       is what lost the sheet on reload. */
    function saveDraft() {
      if (state.view !== 'sheet') return;
      state.sheetDraft = collectSheet();
      /* The one write path every sheet edit already goes through — taps, the
         free-text box and step moves all land here, so the badge cannot fall
         out of step with what the card would show. */
      updatePicksCount();
      save();
    }

    function updateFreeCount() {
      if (!els.free || !els.freeCount) return;
      els.freeCount.textContent = els.free.value.length + ' / ' + (els.free.getAttribute('maxlength') || '300');
    }

    /* ── ONE QUESTION AT A TIME ──────────────────────────────────────────────
       Her sisters ask this way (renderQuestion() in quiz.js) and the first build
       of this sheet did not: it stacked all six questions in one scroller, which
       on a phone opens on a wall of twenty-six choices. That is the exact
       "I don't know, I'll leave" moment the sheet exists to prevent.

       The step INDEX lives in the markup (`data-lq-step`), not in a JS array, so
       adding or reordering a question is a section edit and this code does not
       move. */
    function sheetStepCount() { return els.steps.length; }

    function stepAnswered(index) {
      var pane = els.steps[index];
      if (!pane) return false;
      /* The free-text question is genuinely optional — never block on it. */
      if (pane.querySelector('[data-lq-free]')) return true;
      return pane.querySelectorAll('[data-lq-tap][aria-pressed="true"]').length > 0;
    }

    /* 🚨 DELIBERATELY NOT stepAnswered(). That one answers "may the visitor move
       on", so it short-circuits to TRUE on the free-text pane because that
       question is optional — reusing it here would permanently suppress the
       scroll cue on question 6, which has no taps to select and can still
       overflow. This asks a narrower thing: has anything actually been picked. */
    function stepHasSelection(index) {
      var pane = els.steps[index];
      return !!(pane && pane.querySelectorAll('[data-lq-tap][aria-pressed="true"]').length);
    }

    function renderSheetStep() {
      var total = sheetStepCount();
      if (!total) return;
      state.sheetStep = Math.max(0, Math.min(total - 1, state.sheetStep));
      var at = state.sheetStep;
      var last = at === total - 1;

      els.steps.forEach(function (pane, i) { pane.hidden = i !== at; });

      if (els.stepLabel) els.stepLabel.textContent = 'Pregunta ' + (at + 1) + ' / ' + total;
      if (els.dots) {
        /* Built once and RE-CLASSED after that, so `.lq-dot`'s width/colour
           transition actually runs: rebuilding the spans on every step gave
           the bar brand-new elements with nothing to transition from. */
        if (els.dots.children.length !== total) {
          els.dots.innerHTML = '';
          for (var i = 0; i < total; i++) {
            var dot = document.createElement('span');
            dot.className = 'lq-dot';
            els.dots.appendChild(dot);
          }
        }
        Array.prototype.forEach.call(els.dots.children, function (dot, i) {
          dot.classList.toggle('is-active', i === at);
          dot.classList.toggle('is-done', i < at);
        });
      }
      if (els.sheetBack) els.sheetBack.hidden = at === 0;
      if (els.sheetNext) els.sheetNext.textContent = last ? 'Empezar la conversación' : 'Siguiente';
      syncSheetNav();
      /* A 4-option question and a 7-option one are different heights inside the
         same box, and a ResizeObserver on that box never fires for it. */
      syncScrollCue();
    }

    /* Next is gated on the CURRENT question being answered, which is what makes
       the neutral option on every question load-bearing: "Depende del día",
       "Nada de eso me frena" and "Ninguna me llama" are how someone declines to
       answer without being stuck. */
    function syncSheetNav() {
      var ok = stepAnswered(state.sheetStep);
      if (els.sheetNext) {
        els.sheetNext.disabled = !ok;
        els.sheetNext.setAttribute('aria-disabled', ok ? 'false' : 'true');
      }
      if (els.navHint) els.navHint.hidden = ok;
    }

    function goSheetStep(delta) {
      /* 🚨 While a page is still lifting away the OLD question is the one
         rendered, so Siguiente is still enabled for it: a second tap here would
         advance past the new question before anyone saw it. */
      if (motion.turning()) return;
      var total = sheetStepCount();
      var next = state.sheetStep + delta;
      if (next < 0) return;
      if (next >= total) { leaveSheet(collectSheet()); return; }
      state.sheetStep = next;
      saveDraft();
      /* The page turns in the direction of travel — forward slides left. */
      motion.turn(delta > 0 ? 1 : -1, function () {
        renderSheetStep();
        /* Move the pane back to the top: question five is taller than question
           one and the panel keeps its scroll offset between them. */
        var view = els.views.sheet;
        if (view) view.scrollTop = 0;
      }, function () {
        for (var i = 0; i < els.steps.length; i++) if (!els.steps[i].hidden) return els.steps[i];
        return null;
      });
    }

    /* The start button opens the SHEET now. An empty chat box asks a lost
       visitor to recall; the sheet asks them to recognise. */
    function openSheet() {
      if (state.started) { beginInterview(); return; }
      /* Resume, do not restart. restart() clears both, so "Reiniciar" still means
         question one with nothing ticked. */
      applySheetDraft(state.sheetDraft);
      renderSheetStep();
      setView('sheet');
    }

    /* Both ways out of the sheet land here. `sheet` is null on the skip path,
       which is the request shape the backend already had — that is why the skip
       link costs nothing. */
    function leaveSheet(sheet) {
      state.prequiz = sheet;
      beginInterview();
    }

    function beginInterview() {
      /* Read BEFORE startConversation(), which flips `started` at once — the
         composer is focused only when picking a conversation back up, never on
         the way in from the sheet (a phone would throw its keyboard over her
         first question). The focus waits for the screen half of setView: the
         chat view is still hidden while the sheet lifts away. */
      var resuming = state.started;
      setView('chat', resuming ? function () { if (els.input) els.input.focus(); } : null);
      if (!resuming) startConversation();
    }

    function sendAnswer() {
      if (state.busy || state.remaining <= 0 || !els.input) return;
      var message = els.input.value.trim();
      if (!message) return;

      els.input.value = '';
      autoGrow();
      addMessage('user', message);
      state.remaining -= 1;
      setBusy(true);
      save();

      setTyping(true, 'Lila está leyendo tu respuesta');
      streamSSE(cfg.quizEndpoint, {
        action: 'answer',
        message: message,
        history: state.history.slice(0, -1),
        fingerprint: state.fingerprint,
        visitorName: getVisitorName()
      }, function (stage) {
        if (stage === 'responding') setTyping(true, 'Lila está escribiendo');
      }).then(function (response) {
        setBusy(false);
        setTyping(false);
        /* 🚨 ORDER MATTERS, and it is not the obvious one. Two different shapes
           mean "banned" and they want opposite handling:

             a 403          — success:false, NO reply. Nothing to print, so the
                              takeover is the whole response.
             a banning turn — success:true WITH her last words and banned:true.
                              Those words must reach the screen first; checking
                              the ban up front would swallow them.

           So the early check is gated on there being no reply to show. */
        if (!response || !response.reply) {
          if (checkBanPayload(response)) return;
          throw humanError((response && response.message) || 'No pude continuar la conversación.');
        }
        if (response.success === false) {
          throw humanError(response.reply || response.message || 'No pude continuar la conversación.');
        }
        addMessage('assistant', response.reply);
        responseReady(response);
        save();
        /* She answers, THEN the ban lands — the same order her sisters use. */
        checkBanPayload(response);
      }).catch(function (error) {
        /* A provider failure must not cost the visitor an answer. */
        state.remaining = Math.min(state.messagesMax, state.remaining + 1);
        updateBudget();
        showError(error && error.human ? error.message : undefined);
      });
    }

    /* Everything the browser hands back is untrusted: an unknown designer, a
       repeat, a fourth entry and a Lila all get dropped here rather than
       rendered. */
    function normalizedMatches(rawMatches) {
      var seen = {};
      return (Array.isArray(rawMatches) ? rawMatches : []).map(function (item, index) {
        var key = typeof item === 'string' ? item : (item && (item.designer || item.id));
        key = String(key || '').toLowerCase().trim();
        if (!cfg.designers[key] || seen[key]) return null;
        seen[key] = true;
        return {
          designer: key,
          rank: Number(item && item.rank) || index + 1,
          reason: String((item && item.reason) || '')
        };
      }).filter(Boolean).slice(0, 3);
    }

    /* ── THE SHOWCASE ───────────────────────────────────────────────────────
       One designer at a time, standing in the arena Lila normally occupies.
       The cards this replaced were the weakest thing on the screen: a seal
       glyph and two paragraphs, for a result the whole quiz exists to deliver.

       🔑 The rendering is a PAGER over `state.matches`, not a rebuild — every
       page writes into the same nodes, so nothing re-enters the DOM and the
       sprite element keeps its decoded image between pages. */
    var showAt = 0;

    function designerSprite(key) {
      var d = cfg.designers[key];
      return (d && d.sprite) || '';
    }

    function showDesigner(index) {
      var list = state.matches;
      if (!list.length) return;
      /* Wrap, so the arrows are still useful at either end and a stale index
         from a restored session cannot land out of bounds. */
      showAt = ((index % list.length) + list.length) % list.length;
      var match = list[showAt];
      var designer = cfg.designers[match.designer] || {};

      /* Drives every `--dz*` token at once — the halo, the ring, her name and
         her CTA all recolour from this one attribute. */
      if (els.stage) els.stage.setAttribute('data-designer', match.designer);

      if (els.designerFig) {
        var src = designerSprite(match.designer);
        if (src) {
          if (els.designerFig.getAttribute('src') !== src) els.designerFig.setAttribute('src', src);
          els.designerFig.hidden = false;
        } else {
          /* No art loaded for her yet: hide the img rather than leave a broken
             icon, and let Lila's own figure stand in. */
          els.designerFig.removeAttribute('src');
          els.designerFig.hidden = true;
        }
      }
      /* 🚨 Lila only steps aside when there is actually someone to replace her
         with. Without this, a designer whose sprite has not been set yet gives
         an EMPTY arena — the same "correctly-sized nothing" this file has been
         caught by once already. */
      if (els.root) els.root.classList.toggle('has-dzfig', !!designerSprite(match.designer));

      if (els.showName) els.showName.textContent = designer.name || match.designer;
      if (els.showReason) els.showReason.textContent = match.reason || '';
      if (els.showCta) {
        els.showCta.textContent = designer.cta || '';
        els.showCta.setAttribute('href', designer.url || '#');
      }
      if (els.showCount) {
        els.showCount.textContent = list.length > 1
          ? String(cfg.copy.resultsCounter || '· {i} de {n}')
              .replace('{i}', String(showAt + 1)).replace('{n}', String(list.length))
          : '';
      }

      if (els.showNav) els.showNav.hidden = list.length < 2;
      if (els.showPrev) els.showPrev.disabled = false;
      if (els.showNext) els.showNext.disabled = false;

      if (els.showDots) {
        /* Rebuilt only when the COUNT changes; re-creating buttons on every
           page would drop focus mid-keyboard-navigation. */
        if (els.showDots.children.length !== list.length) {
          els.showDots.innerHTML = '';
          list.forEach(function (item, i) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'lq-show-dot';
            dot.setAttribute('role', 'tab');
            var dname = (cfg.designers[item.designer] || {}).name || item.designer;
            dot.setAttribute('aria-label', dname);
            dot.addEventListener('click', function () { pageDesigner(i, false); });
            els.showDots.appendChild(dot);
          });
        }
        Array.prototype.forEach.call(els.showDots.children, function (dot, i) {
          dot.classList.toggle('is-on', i === showAt);
          dot.setAttribute('aria-selected', i === showAt ? 'true' : 'false');
        });
      }
    }

    /* Paging WITH the turn (setupMotion's page()). `relative` steps from
       wherever the pager stands WHEN THE TURN LANDS, not when it was asked
       for: two quick arrow taps are two pages, where a pre-computed index
       would have shown the same girl twice. */
    function pageDesigner(target, relative) {
      if (!state.matches.length) return;
      if (!relative && target === showAt) return;
      var dir = relative ? (target < 0 ? -1 : 1) : (target < showAt ? -1 : 1);
      motion.page(dir, function () {
        showDesigner(relative ? showAt + target : target);
      }, function () {
        return [els.designerFig, els.showName, els.showReason, els.showCta];
      });
    }

    function renderResults(presentation, rawMatches) {
      state.presentation = String(presentation || '');
      state.matches = normalizedMatches(rawMatches);
      if (!state.matches.length) {
        showError('No pude encontrar una conexión clara todavía. Tu intento sigue disponible.');
        return;
      }
      /* Every girl of the letter starts loading now, so a page turn finds her
         drawing already decoded instead of turning to an empty arena. */
      state.matches.forEach(function (match) {
        var src = designerSprite(match.designer);
        if (!src) return;
        var img = new Image();
        img.decoding = 'async';
        img.src = src;
      });

      setLilaText(els.presentation, state.presentation);
      /* An empty `presentation` is a real server response (the no-match path),
         and an opinion button that opens a blank card is worse than no button.
         Her sisters hide theirs on the same condition — which is why
         `.lq-opinion[hidden]` carries !important, or the breakpoint blocks
         would keep painting it. */
      if (els.opinion) els.opinion.hidden = !state.presentation;
      showAt = 0;
      showDesigner(0);

      if (els.showLock) els.showLock.hidden = false;
      setBusy(false);
      setView('results');
      save();
    }

    /* ── WHILE SHE WRITES ────────────────────────────────────────────────────
       Ported from `quiz.js:2226` — her sisters' searching screen. TWO MOVING
       PARTS, AND THEY ARE DELIBERATELY NOT WIRED TOGETHER: the rail advances
       on the server's stage events, the status line walks its own phrasings on
       a timer. Stages arrive when they arrive — sometimes seconds apart,
       sometimes three at once — so nothing that has to keep moving may hang
       off them, or the screen freezes for whole seconds at a time and reads as
       broken. This separation is the entire design; do not "simplify" it by
       driving the text from the stage events. */
    var workTimer = null;
    var workOrder = (cfg.copy && cfg.copy.workStages) || [];
    var workLineMs = Math.max(1200, parseInt(cfg.copy && cfg.copy.workLineMs, 10) || 2600);

    function buildWorkSteps() {
      if (!els.workRail || !workOrder.length) return;
      els.workRail.innerHTML = '';
      workOrder.forEach(function () {
        var s = document.createElement('span');
        s.className = 'lq-work-step';
        els.workRail.appendChild(s);
      });
    }

    /** Fill the rail up to and including `stage`. A stage that is not in
        `workOrder` leaves it exactly where it was rather than blanking it, so
        adding a step server-side can never break the display. */
    function markWorkStage(stage) {
      if (!els.workRail) return;
      var at = workOrder.indexOf(stage);
      if (at < 0) return;
      var steps = els.workRail.children;
      for (var i = 0; i < steps.length; i++) {
        steps[i].classList.toggle('is-done', i < at);
        steps[i].classList.toggle('is-now', i === at);
      }
    }

    /** Every phrasing a stage can show. One string still works: the Liquid
        splits on "|" and a single value arrives as a one-item list. */
    function stageLines(stage) {
      var v = (cfg.copy.workingTexts || {})[stage];
      if (!v) return null;
      var pool = (Array.isArray(v) ? v : [v])
        .map(function (s) { return String(s).trim(); })
        .filter(Boolean);
      return pool.length ? pool : null;
    }

    function stopWorkLines() {
      if (workTimer) { window.clearInterval(workTimer); workTimer = null; }
      if (els.workText) els.workText.classList.remove('is-swapping');
    }

    /** Put `stage` on the status line and walk its phrasings while it lasts. */
    function setStageText(stage) {
      var pool = stageLines(stage);
      if (!pool || !els.workText) return;
      stopWorkLines();
      els.workText.textContent = pool[0];
      if (pool.length < 2 || reduce) return;
      var i = 0;
      workTimer = window.setInterval(function () {
        els.workText.classList.add('is-swapping');
        window.setTimeout(function () {
          i = (i + 1) % pool.length;
          els.workText.textContent = pool[i];
          els.workText.classList.remove('is-swapping');
        }, 260);                     /* matches the .26s opacity transition */
      }, workLineMs);
    }

    /** Everything the sealing screen needs on the way in, in one call so the
        mock and the real path cannot drift apart. */
    function beginWorking() {
      setView('sealing');
      buildWorkSteps();
      markWorkStage(workOrder[0]);
      setStageText(workOrder[0]);
    }

    function finishInterview() {
      if (state.busy || !state.ready) return;
      setBusy(true);
      beginWorking();

      /* 🔑 ONE FREE RETRY, and only on the flag the SERVER sets.

         A judge that runs long consumes the whole 56s function budget, so the
         backend has nothing left to retry into -- only a fresh request gets a
         fresh 60s. Measured 2026-08-23 on one identical transcript: 56.7s
         killed, then 27.7s delivered. Until now the visitor was shown "problema
         tecnico" and asked to press the button again themselves, for a failure
         that had already cost them nothing.

         Free is literal: the entitlement is claimed only AFTER the judge
         succeeds, so a failed match leaves try_used false. A second request
         spends one of the five attempts-per-hour and nothing else.

         ONE, never a loop -- a loop would burn that guard and leave someone
         watching the searching screen forever. And `retryable` is stated by the
         server rather than inferred from the absence of other flags, so a
         refusal that waiting cannot fix (locked, cooldown, needs_interview) is
         never retried into a second refusal. */
      function askForMatch(attempt) {
        return streamSSE(cfg.matchEndpoint, {
          history: state.history.slice(-40),
          fingerprint: state.fingerprint,
          /* The sheet goes to the judge too, and the BACKEND decides how much of
             it counts: it renders the evidence half -- what they want to happen
             in a room, what the garment is for, what makes them leave one on the
             hanger, and anything they typed -- and drops the colour swatches and
             the world tap, which are votes rather than evidence. Sending the
             whole object is correct: the filtering belongs next to the sheet
             definition, not here, or the two would drift and the theme would
             quietly decide what the judge is allowed to know. */
          /* 🔑 THE RETRY DROPS THE SHEET, and this is measured rather than
             guessed. On 2026-08-23 one ambiguous transcript failed at 56.3s
             TWICE with the sheet attached, and the same transcript with no sheet
             completed in 51.8s. An identical second attempt is no use against a
             case that is systematically slow rather than unlucky: it just waits
             another 56 seconds to fail the same way.

             The sheet is SUPPORTING evidence by construction — it may confirm or
             eliminate, never carry a name alone — so it is the cheapest thing in
             the request to give up, and giving it up is what buys the visitor a
             reading instead of an error. The conversation, which is what the
             verdict actually rests on, is untouched. */
          /* 🔑 THE RETRY DROPS THE TAPS AND KEEPS WHAT THEY WROTE.

             Measured 2026-08-23: an ambiguous transcript failed at 56.3s twice
             with the whole sheet and completed at 51.8s without it, so shedding
             the sheet is what buys a reading. But dropping ALL of it threw away
             the free-text box too — the smallest and most evidence-dense part —
             and in a later run that cost the judge the only gender marker in the
             session: it closed with «lo vemos juntas» inferred from Lila's own
             words instead.

             So the retry now sheds the tap lines and their weighting rules
             (~237 tokens) and keeps the few dozen the visitor actually typed.
             Better trade in both directions: more headroom than the taps were
             worth, and none of the evidence. */
          prequiz: attempt === 1
            ? state.prequiz
            : (state.prequiz && state.prequiz.libre ? { libre: state.prequiz.libre } : null)
        }, function (stage) {
          setStageText(stage);
          markWorkStage(stage);
        }).then(function (response) {
          if (attempt === 1 && response && response.success === false && response.retryable === true) {
            /* Silent on purpose. The searching screen is still telling the
               truth -- it IS still working on their reading -- and announcing a
               stumble would turn a longer wait into a visible fault. */
            return askForMatch(2);
          }
          return response;
        });
      }

      askForMatch(1).then(function (response) {
        stopWorkLines();
        if (checkBanPayload(response)) return;
        if (!response || response.success === false) {
          throw humanError((response && (response.presentation || response.message)) || 'No pude terminar la lectura.');
        }
        state.retryAfter = Number(response.retry_after) || null;
        state.lockEndsAt = state.retryAfter ? Date.now() + state.retryAfter * 1000 : null;
        renderResults(response.presentation, response.matches);
      }).catch(function (error) {
        showError(error && error.human ? error.message : undefined);
      });
    }

    function updateCountdown() {
      if (!state.lockEndsAt) return;
      var remaining = Math.max(0, Math.ceil((state.lockEndsAt - Date.now()) / 1000));
      var hours = Math.floor(remaining / 3600);
      var minutes = Math.floor((remaining % 3600) / 60);
      var seconds = remaining % 60;
      if (els.hours) els.hours.textContent = String(hours).padStart(2, '0');
      if (els.minutes) els.minutes.textContent = String(minutes).padStart(2, '0');
      if (els.seconds) els.seconds.textContent = String(seconds).padStart(2, '0');
      if (remaining <= 0) {
        window.clearInterval(state.lockTimer);
        state.lockTimer = null;
      }
    }

    /* ── THE SENT LETTERS (2026-10-02) ──────────────────────────────────────
       On the pause Lila is back in the arena, so the girls of the reading move
       onto the sheet: one envelope each — «Para Akira», her stamp (her drawing
       on her colour) cancelled by Lila's postmark, the whole card a link to her
       quiz. Rebuilt from `state.matches` on every visit; with no reading (another
       device, cleared storage) the row stays hidden. */
    var POSTMARK = '<svg class="lq-sent-pm" viewBox="0 0 60 32" aria-hidden="true" focusable="false">' +
      '<circle cx="16" cy="16" r="12"/><circle cx="16" cy="16" r="8.8" opacity=".55"/>' +
      '<path d="M30 12q3.5-3 7 0t7 0t7 0t7 0M30 20q3.5-3 7 0t7 0t7 0t7 0"/></svg>';

    function renderSent() {
      if (!els.sent) return;
      els.sent.innerHTML = '';
      state.matches.forEach(function (match) {
        var d = cfg.designers[match.designer];
        if (!d) return;
        var card = document.createElement('a');
        card.className = 'lq-sent-card';
        card.href = d.url || '#';
        card.setAttribute('data-designer', match.designer);

        var txt = document.createElement('span');
        txt.className = 'lq-sent-txt';
        [['lq-sent-to', cfg.copy.sentTo || 'Para'], ['lq-sent-nm', d.name || match.designer],
         ['lq-sent-go', cfg.copy.sentCta || 'Hacer su quiz']].forEach(function (part) {
          var s = document.createElement('span');
          s.className = part[0];
          s.textContent = part[1];
          txt.appendChild(s);
        });

        var stamp = document.createElement('span');
        stamp.className = 'lq-sent-stamp';
        stamp.setAttribute('aria-hidden', 'true');
        var field = document.createElement('span');
        field.className = 'lq-sent-field';
        var src = d.thumb || d.sprite;
        if (src) {
          var img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.decoding = 'async';
          field.appendChild(img);
        }
        stamp.appendChild(field);
        stamp.insertAdjacentHTML('beforeend', POSTMARK);

        card.appendChild(txt);
        card.appendChild(stamp);
        els.sent.appendChild(card);
      });
      els.sent.setAttribute('data-n', String(els.sent.children.length));
      els.sent.hidden = !els.sent.children.length;
    }

    function showLocked(seconds) {
      var safeSeconds = Math.max(1, Number(seconds) || 172800);
      if (!state.lockEndsAt || state.lockEndsAt <= Date.now()) {
        state.lockEndsAt = Date.now() + safeSeconds * 1000;
      }
      if (els.backResults) els.backResults.hidden = !state.matches.length;
      renderSent();
      setView('locked');
      updateCountdown();
      window.clearInterval(state.lockTimer);
      state.lockTimer = window.setInterval(updateCountdown, 1000);
      save();
    }

    function readStatus() {
      return fetch(cfg.quizEndpoint + '?fingerprint=' + encodeURIComponent(state.fingerprint || ''))
        .then(function (response) { return response.json(); })
        .then(function (status) {
          if (checkBanPayload(status)) return status;
          if (Number.isFinite(Number(status.messages_remaining))) state.remaining = Number(status.messages_remaining);
          if (Number.isFinite(Number(status.messages_max))) state.messagesMax = Number(status.messages_max);
          updateBudget();
          if (status.locked || status.try_used) {
            state.retryAfter = Number(status.retry_after) || 172800;
            state.lockEndsAt = Date.now() + state.retryAfter * 1000;
            if (state.matches.length) renderResults(state.presentation, state.matches);
            else showLocked(state.retryAfter);
            return status;
          }
          if (state.started) beginInterview();
          return status;
        }).catch(function () {
          return null;
        });
    }

    /* ── LOCAL PREVIEW HOOK ──────────────────────────────────────────────
       The quiz carries no sample data of its own any more: production always
       talks to the backend, and there is no mock switch to leave on by
       accident. This is what replaced it.

       🚨 Gated on hostname. On the shop domain `window.GOK_LILA_QUIZ_PREVIEW`
       is never read and this returns false on its first line, so the screens
       below are unreachable there.

       The sample presentation and matches live with the DEV STATE BAR in the
       section, which is stripped before every push - so the fake copy exists
       only on a machine running `theme dev`. Reaching the result screen
       against the real backend costs the visitor their one reading per 48
       hours, which is why a local path to it is worth keeping at all. */
    function devPreview() {
      if (!/^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$/.test(location.hostname)) return false;
      var sample = window.GOK_LILA_QUIZ_PREVIEW;
      if (!sample) return false;

      var params = new URLSearchParams(window.location.search);
      var wanted = params.get('lq_state');
      if (!wanted) return false;
      var count = Math.min(3, Math.max(1, parseInt(params.get('lq_n'), 10) || 2));

      if (wanted === 'intro') {
        /* 🚨 restart(), not setView(): the page restores its last screen from
           localStorage, so "go to intro" otherwise lands on `chat` for anyone
           who has already started. That has mislabelled measurements here. */
        restart();
        return true;
      }
      if (wanted === 'results' || wanted === 'locked') {
        var matches = (sample.matches || []).slice(0, count);
        var presentation = (sample.presentations || [])[count - 1] || '';
        state.ready = true;
        state.retryAfter = sample.retry_after || 172800;
        state.lockEndsAt = Date.now() + state.retryAfter * 1000;
        if (wanted === 'locked') {
          state.matches = normalizedMatches(matches);
          state.presentation = presentation;
          showLocked(state.retryAfter);
        } else {
          renderResults(presentation, matches);
        }
        return true;
      }
      if (wanted === 'sealing') {
        beginWorking();
        if (!reduce) {
          workOrder.slice(1).forEach(function (stage, i) {
            window.setTimeout(function () {
              if (state.view !== 'sealing') return;
              markWorkStage(stage);
              setStageText(stage);
            }, 4200 * (i + 1));
          });
        }
        return true;
      }
      if (wanted === 'error') {
        showError(params.get('lq_msg') || undefined);
        return true;
      }
      return false;
    }

    if (els.input) {
      els.input.addEventListener('input', autoGrow);
      els.input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          sendAnswer();
        }
      });
    }
    if (els.send) els.send.addEventListener('click', sendAnswer);
    $$('[data-lq-restart]').forEach(function (button) {
      button.addEventListener('click', restart);
    });
    if (els.start) els.start.addEventListener('click', openSheet);
    els.taps.forEach(function (button) {
      button.addEventListener('click', function () {
        /* Firmer than the nav buzz: this one commits an answer. */
        tapHaptic(14);
        toggleTap(button);
        /* A tap can only ever satisfy the question it belongs to, but it can also
           UNDO one (re-tapping clears), so the nav has to be re-derived either way. */
        syncSheetNav();
        /* Same "either way": picking hides the cue, un-picking brings it back. */
        syncScrollCue();
        saveDraft();
      });
    });
    if (els.free) els.free.addEventListener('input', function () { updateFreeCount(); saveDraft(); });
    if (els.sheetBack) els.sheetBack.addEventListener('click', function () { goSheetStep(-1); });
    if (els.sheetNext) els.sheetNext.addEventListener('click', function () { goSheetStep(1); });
    renderSheetStep();
    if (els.finish) els.finish.addEventListener('click', finishInterview);

    /* Every other control the visitor can press, at the lighter strength.
       🚨 Deliberately excludes els.taps, which already fire tapHaptic(14) -- an
       element in both lists buzzes twice and reads as a stutter. */
    [els.start, els.sheetBack, els.sheetNext, els.finish, els.send,
     els.colorMark, els.popClose, els.popLink, els.note, els.leadNote, els.opinion]
      .concat($$('[data-lq-restart]'))
      .filter(Boolean)
      .forEach(function (el) {
        el.addEventListener('click', function () { tapHaptic(10); });
      });
    if (els.retry) els.retry.addEventListener('click', beginInterview);
    if (els.showLock) els.showLock.addEventListener('click', function () {
      showLocked(state.retryAfter || 172800);
    });
    if (els.backResults) els.backResults.addEventListener('click', function () {
      renderResults(state.presentation, state.matches);
    });

    /* ── Paging the showcase ─────────────────────────────────────────────── */
    if (els.showPrev) els.showPrev.addEventListener('click', function () { pageDesigner(-1, true); });
    if (els.showNext) els.showNext.addEventListener('click', function () { pageDesigner(1, true); });
    /* Arrow keys, the way the journal takes them. Scoped to the result screen
       and skipped while a field or the note dialog has focus, so it can never
       eat a keystroke meant for something else. */
    root.addEventListener('keydown', function (event) {
      if (state.view !== 'results' || state.matches.length < 2) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      var tag = (event.target && event.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || noteIsOpen()) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); pageDesigner(-1, true); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); pageDesigner(1, true); }
    });

    if (new URLSearchParams(window.location.search).get('lq_reset') === '1') {
      try { localStorage.removeItem(storeKey); } catch (error) {}
    }

    /* ── Off-screen ───────────────────────────────────────────────────
       Nothing infinite runs while she is not on screen — the same contract
       the other three quiz sections keep. The bubble tracker is a per-frame
       rAF and is the most expensive thing here to leave running unseen.

       🚨 "NOT ON SCREEN" IS TWO INDEPENDENT THINGS, AND THIS ONLY HANDLED ONE.
          Scrolled away was covered; SWITCHED TABS was not. The flags are OR'd
          rather than one being derived from the other, so neither can clear the
          other's reason: scrolling away, switching tab, then coming BACK to the
          tab must still leave her still, because she is still off screen.
       ⚠️ The win is smaller than it looks — a background tab already has its rAF
          paused by the browser and its `setInterval` clamped to ~1/s — but the
          flipbook and the ban timer do keep ticking at that clamped rate, and
          `.is-lq-still` was never applied at all, so her CSS animations kept
          their clocks running. Cheap to close, so it is closed. */
    var lqOffScreen = false;
    var lqTabHidden = document.visibilityState === 'hidden';

    function applyStill() {
      var still = lqOffScreen || lqTabHidden;
      root.classList.toggle('is-lq-still', still);
      if (player) { if (still) player.suspend(); else player.resume(); }
      /* A flipbook is a setInterval, which `.is-lq-still` cannot reach —
         CSS pauses CSS animations, not timers. Without this a grouped pose
         keeps stepping frames for the life of the page, off screen. */
      if (poses && poses.suspend) { if (still) poses.suspend(); else poses.resume(); }
      /* Nothing to stop for the signature: it is a one-shot that ends by
         itself, and `.is-lq-still` pauses it in CSS if she is scrolled away
         mid-stroke. There is no longer a canvas here to start or stop. */
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          lqOffScreen = !entry.isIntersecting;
        });
        applyStill();
      }, { rootMargin: '120px' }).observe(root);
    }

    /* ⚠️ Registered even without IntersectionObserver, so a tab switch still
       parks her on a browser old enough to lack it. */
    document.addEventListener('visibilitychange', function () {
      lqTabHidden = document.visibilityState === 'hidden';
      applyStill();
    });

    /* Her **bold** is a settings-level device, not just a runtime one: the
       copy in the theme editor uses it too, and Liquid prints it verbatim.
       Parse the Liquid-authored prose once on init so an editor writing
       **esto** gets bold instead of two asterisks. */
    $$('[data-lq-md]').forEach(function (el) { setLilaText(el, el.textContent); });

    restore();
    rebuildMessages();
    updateBudget();
    /* Resume the sheet when that is where they were. Anything already started
       still wins -- the conversation is further along than the sheet ever is. */
    if (state.started && state.history.length) {
      setView('chat');
    } else if (state.view === 'sheet' && (state.sheetDraft || state.sheetStep)) {
      applySheetDraft(state.sheetDraft);
      renderSheetStep();
      setView('sheet');
    } else {
      setView('intro');
    }

    getFingerprint().then(function (fingerprint) {
      state.fingerprint = fingerprint;
      if (devPreview()) return;
      if (state.matches.length && state.lockEndsAt && state.lockEndsAt > Date.now()) {
        renderResults(state.presentation, state.matches);
        return;
      }
      if (state.history.length) {
        state.started = true;
        responseReady({ ready: state.ready, messages_remaining: state.remaining });
        beginInterview();
      }
      readStatus();
    });
  }

  function initAll(scope) {
    var configs = window.GOK_LILA_QUIZ_CONFIG || {};
    Object.keys(configs).forEach(function (key) {
      var cfg = configs[key];
      var root = document.getElementById(cfg.rootId);
      if (root && (!scope || scope.contains(root) || root === scope)) init(key, cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
